import { sanitizeRichTextHtml } from './utils/sanitizeHtml';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Alert, Typography, Space, Input, Select, Button, Tag, message, Spin, Modal, Divider, Tooltip as AntTooltip, Checkbox } from 'antd';
import type { TourProps } from 'antd';
import { ReloadOutlined, SaveOutlined, BookOutlined, PlusOutlined, QuestionCircleOutlined, ArrowLeftOutlined, DeleteOutlined, EditOutlined, CopyOutlined } from '@ant-design/icons';
import { getAuth } from 'firebase/auth';
import styled from 'styled-components';
import {
  getAllLanguages,
  getDefaultQuotationStyle,
  getQuotationStyle,
  hasQuotationOptions,
  isLanguageBeta,
} from './LanguageConfig';
import { useTourFlags } from './tours/useTourFlags';
import AppTour, { renderAppTourPanel } from './tours/AppTour';
import AppPageHeader from './components/AppPageHeader';
import AIModelSelect from './AIModelSelect';
import ProcessLoadingCard from './components/ProcessLoadingCard';
import './SeriesManagementPage.css';

import type { SeriesBook, SeriesMetadata } from './types';
import { requestWithRetry } from './utils/requestWithRetry';
import { isValidGlossaryTerm } from './utils/glossaryValidation';
import { getAuthHeaders } from './utils/authHeaders';
import { normalizeTargetLangKey } from './utils/glossaryKeys';
import { getTargetLanguageFromPair, splitLanguagePair } from './utils/languagePairs';
import { metadataTranslationFieldKey } from './utils/metadataFieldKeys';
import { MAX_BLURB_TRANSLATION_WORDS } from './utils/metadataFreeRuns';
import { countWordsIncludingMarkup } from './utils/wordcount';
import { resolveFrontendApiBase } from './utils/apiBase';
import {
  INTENDED_AUDIENCE_PRESETS,
  enforceNonfictionAudienceExclusivity,
  splitAndDedupeAudienceValues,
} from './utils/intendedAudience';
import { useTutorialSandbox } from './tutorials/TutorialSandboxProvider';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const MULTI_ENTRY_TOOLTIP =
  'Type each item individually or paste a comma-separated list. Duplicate values are removed automatically.';
const METADATA_REFRESH_FIELDS = ['intendedAudience', 'genre', 'tropes', 'keywords'] as const;
const TRANSLATE_REFRESH_FIELDS = ['alwaysTranslate', 'neverTranslate'] as const;
const REFRESHABLE_FIELDS = [...METADATA_REFRESH_FIELDS, ...TRANSLATE_REFRESH_FIELDS] as const;
const GLOSSARY_PREVIEW_LIMIT = 10;
type MetadataRefreshField = (typeof METADATA_REFRESH_FIELDS)[number];
type TranslateRefreshField = (typeof TRANSLATE_REFRESH_FIELDS)[number];
type RefreshableField = MetadataRefreshField | TranslateRefreshField;
type LoadingContext = 'initial' | 'refresh' | null;
const REFRESH_FIELD_LABELS: Record<RefreshableField, string> = {
  intendedAudience: 'Intended Audience',
  genre: 'Genres',
  tropes: 'Tropes',
  keywords: 'Keywords',
  alwaysTranslate: 'Glossary: Always translate',
  neverTranslate: 'Glossary: Never translate',
};

const API_BASE = resolveFrontendApiBase();

const normalizeSeriesInput = (value: string | null | undefined): string =>
  (value || '').replace(/\s+/g, ' ').trim();

type DefaultFields = {
  author?: string;
  genre?: string[];
  tropes?: string[];
  keywords?: string[];
  intendedAudience?: string[];
  seriesDescription?: string;
};

type AggregatedData = DefaultFields & {
  name?: string;
};

type TranslateRules = {
  alwaysTranslate: Record<string, string[]>;
  neverTranslate: Record<string, string[]>;
};

type SeriesTranslationBookDetail = SeriesBook & {
  language?: string;
  genre?: string[];
  genres?: string[];
  intendedAudience?: string[];
};

export const resolveSeriesDescriptionTranslation = (
  translations: SeriesMetadata['translations'] | undefined,
  sourceLanguage: string,
  targetLanguage: string,
): string => {
  if (!sourceLanguage || !targetLanguage) return '';
  const descriptions = translations?.seriesDescription || {};
  const candidates = [
    `${sourceLanguage}-${targetLanguage}`,
    `${sourceLanguage}_${targetLanguage}`,
    metadataTranslationFieldKey('seriesDescription', sourceLanguage, targetLanguage),
  ];
  for (const key of candidates) {
    const value = descriptions[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return '';
};

const cleanString = (value?: string | null) => (typeof value === 'string' ? value.trim() : '');
const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const detail = (error as any)?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
  const responseMessage = (error as any)?.response?.data?.message;
  if (typeof responseMessage === 'string' && responseMessage.trim()) return responseMessage;
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
};
const uniqStrings = (values: Array<string | undefined | null>): string[] => {
  const set = new Set<string>();
  values.forEach(val => {
    const cleaned = cleanString(val);
    if (cleaned) set.add(cleaned);
  });
  return Array.from(set);
};

const flattenUnique = (batches: Array<string[] | undefined | null>): string[] => {
  const set = new Set<string>();
  batches.forEach(arr => {
    if (!arr) return;
    arr.forEach(item => {
      const cleaned = cleanString(item);
      if (cleaned) set.add(cleaned);
    });
  });
  return Array.from(set);
};

export const splitAndDedupeList = (values: string[]): string[] => {
  return splitAndDedupeAudienceValues(values);
};
export const ensureStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return splitAndDedupeList(
      (value as unknown[]).map(item => (typeof item === 'string' ? item : String(item ?? '')))
    );
  }
  if (typeof value === 'string') {
    return splitAndDedupeList([value]);
  }
  return [];
};
const EMPTY_AGGREGATED: AggregatedData = {
  genre: [],
  tropes: [],
  keywords: [],
  intendedAudience: [],
};

const sanitizeLanguageToken = (value: string) => value.replace(/\s+/g, '0').trim();

const buildStoredPairKey = (pair: string): string | null => {
  const { sourceLang, targetLang } = splitLanguagePair(pair);
  if (!sourceLang || !targetLang) return null;
  const source = sanitizeLanguageToken(sourceLang);
  const target = sanitizeLanguageToken(targetLang);
  if (!source || !target) return null;
  return `${source}_${target}`;
};

const extractTargetLanguage = (pair: string): string | null => {
  const target = getTargetLanguageFromPair(pair);
  const normalized = normalizeTargetLangKey(target);
  return normalized || null;
};

const displayTargetLanguageKey = (value: string): string => value.replace(/_/g, ' ');

export const aggregateTranslateRulesFromBooks = (details: any[]): TranslateRules => {
  const aggregated: TranslateRules = { alwaysTranslate: {}, neverTranslate: {} };
  const alwaysBuckets = new Map<string, Map<string, string>>();
  const neverBuckets = new Map<string, Map<string, string>>();

  const addAlwaysPair = (language: string, entry: string) => {
    const [rawSource = '', rawTarget = ''] = entry.split('|');
    const source = rawSource.trim();
    const target = rawTarget.trim();
    if (!source || !target) return;
    const normalized = `${source.toLocaleLowerCase()}|${target.toLocaleLowerCase()}`;
    const bucket = alwaysBuckets.get(language) ?? new Map<string, string>();
    if (!bucket.has(normalized)) {
      bucket.set(normalized, `${source} | ${target}`);
      alwaysBuckets.set(language, bucket);
    }
  };

  const addNeverTerm = (language: string, value: string) => {
    const cleaned = value.trim();
    if (!cleaned) return;
    const normalized = cleaned.toLocaleLowerCase();
    const bucket = neverBuckets.get(language) ?? new Map<string, string>();
    if (!bucket.has(normalized)) {
      bucket.set(normalized, cleaned);
      neverBuckets.set(language, bucket);
    }
  };

  details.forEach(book => {
    if (!book) return;
    const translationPairs: string[] = book.translation_pairs || book.translationPairs || [];
    translationPairs.forEach(pair => {
      const storedKey = buildStoredPairKey(pair);
      const targetLanguage = extractTargetLanguage(pair);
      if (!storedKey || !targetLanguage) return;
      const alwaysSource =
        (book.alwaysTranslatePairs && book.alwaysTranslatePairs[pair]) ||
        book[`always_translate_${storedKey}`] ||
        [];
      const neverSource =
        (book.neverTranslatePairs && book.neverTranslatePairs[pair]) ||
        book[`never_translate_${storedKey}`] ||
        [];

      alwaysSource.forEach((entry: string) => addAlwaysPair(targetLanguage, entry));
      neverSource.forEach((entry: string) => addNeverTerm(targetLanguage, entry));
    });
  });

  alwaysBuckets.forEach((bucket, language) => {
    aggregated.alwaysTranslate[language] = Array.from(bucket.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  });
  neverBuckets.forEach((bucket, language) => {
    aggregated.neverTranslate[language] = Array.from(bucket.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  });

  return aggregated;
};

export const aggregateDefaultsFromBooks = (details: any[]): AggregatedData => {
  if (!details.length) return { ...EMPTY_AGGREGATED };
  const authors = uniqStrings(details.map((b: any) => b.author));
  const descriptions = uniqStrings(details.map((b: any) => b.seriesDescription));
  return {
    name: uniqStrings(details.map((b: any) => b.series))[0],
    seriesDescription: descriptions[0],
    author: authors[0],
    genre: flattenUnique(details.map((b: any) => b.genre || b.genres)),
    tropes: flattenUnique(details.map((b: any) => b.tropes)),
    keywords: flattenUnique(details.map((b: any) => b.keywords)),
    intendedAudience: flattenUnique(details.map((b: any) => ensureStringArray(b.intendedAudience))),
  };
};

const translateRulesHaveValues = (rules?: TranslateRules | null) => {
  if (!rules) return false;
  const hasAlways = Object.values(rules.alwaysTranslate || {}).some(values => (values || []).length > 0);
  if (hasAlways) return true;
  return Object.values(rules.neverTranslate || {}).some(values => (values || []).length > 0);
};

const isMetadataRefreshField = (value: RefreshableField): value is MetadataRefreshField =>
  (METADATA_REFRESH_FIELDS as readonly string[]).includes(value);

const isTranslateRefreshField = (value: RefreshableField): value is TranslateRefreshField =>
  (TRANSLATE_REFRESH_FIELDS as readonly string[]).includes(value);

const isValidTerm = isValidGlossaryTerm;
const normalizeTerm = (value: string) => value.trim().toLocaleLowerCase();
const areTermsEquivalent = (a: string, b: string) => normalizeTerm(a) === normalizeTerm(b);
const getPairKey = (source: string, target: string) => `${normalizeTerm(source)}|${normalizeTerm(target)}`;
const splitGlossaryTerms = (value: string) =>
  value
    .split(/\n|,|\|/)
    .map(entry => entry.trim())
    .filter(Boolean);
const getNeverTermSet = (terms: string[]) =>
  new Set(terms.map(term => normalizeTerm(term)).filter(Boolean));
const getAlwaysSourceSet = (pairs: string[]) =>
  new Set(
    pairs
      .map(pair => pair.split('|')[0]?.trim() ?? '')
      .map(source => normalizeTerm(source))
      .filter(Boolean)
  );
const hasValue = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

export const fillEmptyFieldsWithSuggestions = (
  current: AggregatedData,
  suggestions: AggregatedData
): AggregatedData => ({
  ...current,
  seriesDescription: current.seriesDescription || suggestions.seriesDescription,
  author: current.author || suggestions.author,
  genre: current.genre && current.genre.length > 0 ? [...current.genre] : [...(suggestions.genre || [])],
  tropes: current.tropes && current.tropes.length > 0 ? [...current.tropes] : [...(suggestions.tropes || [])],
  keywords: current.keywords && current.keywords.length > 0 ? [...current.keywords] : [...(suggestions.keywords || [])],
  intendedAudience:
    current.intendedAudience && current.intendedAudience.length > 0
      ? [...current.intendedAudience]
      : [...(suggestions.intendedAudience || [])],
});

type AlwaysPairError = 'missing' | 'identical' | 'invalid' | 'duplicate';

type AlwaysPairResult =
  | { kind: 'error'; error: AlwaysPairError }
  | { kind: 'success'; list: string[] };

export const buildNextAlwaysPairs = (
  existingList: string[],
  rawSource: string,
  rawTarget: string
): AlwaysPairResult => {
  const source = rawSource.trim();
  const target = rawTarget.trim();
  if (!source || !target) {
    return { kind: 'error', error: 'missing' };
  }
  if (areTermsEquivalent(source, target)) {
    return { kind: 'error', error: 'identical' };
  }
  if (!isValidTerm(source) || !isValidTerm(target)) {
    return { kind: 'error', error: 'invalid' };
  }

  const normalizedKey = getPairKey(source, target);
  const duplicate = existingList.some(pair => {
    const [existingSource = '', existingTarget = ''] = pair.split('|').map(part => part.trim());
    return getPairKey(existingSource, existingTarget) === normalizedKey;
  });
  if (duplicate) {
    return { kind: 'error', error: 'duplicate' };
  }

  return {
    kind: 'success',
    list: [...existingList, `${source} | ${target}`],
  };
};

type BulkImportError = 'emptyInput' | 'noEntries' | 'noNewPairs';
type BulkImportResult =
  | { kind: 'error'; error: BulkImportError }
  | {
    kind: 'success';
    pairsToAdd: string[];
    invalidEntries: Array<{ entry: string; reason: string }>;
    duplicateEntries: string[];
  };

export const parseBulkAlwaysImport = (
  existingList: string[],
  rawInput: string,
  options?: { conflictSources?: Set<string> }
): BulkImportResult => {
  const trimmedInput = rawInput.trim();
  if (!trimmedInput) {
    return { kind: 'error', error: 'emptyInput' };
  }

  const entries = trimmedInput
    .split(/\n|\|/)
    .map(entry => entry.trim())
    .filter(Boolean);

  if (!entries.length) {
    return { kind: 'error', error: 'noEntries' };
  }

  const existingKeys = new Set(
    existingList.map(pair => {
      const [source = '', target = ''] = pair.split('|').map(part => part.trim());
      return getPairKey(source, target);
    })
  );

  const pairsToAdd: string[] = [];
  const invalidEntries: Array<{ entry: string; reason: string }> = [];
  const duplicateEntries: string[] = [];

  entries.forEach(entry => {
    const [rawSource = '', rawTarget = ''] = entry.split('~');
    const source = rawSource.trim();
    const target = rawTarget.trim();

    if (!source || !target) {
      invalidEntries.push({ entry, reason: 'Missing source or target' });
      return;
    }

    if (areTermsEquivalent(source, target)) {
      invalidEntries.push({ entry, reason: 'Source and target are the same' });
      return;
    }

    if (!isValidTerm(source) || !isValidTerm(target)) {
      invalidEntries.push({ entry, reason: 'Contains unsupported characters' });
      return;
    }

    const normalizedSource = normalizeTerm(source);
    if (options?.conflictSources?.has(normalizedSource)) {
      invalidEntries.push({ entry, reason: 'Source conflicts with Never Translate glossary' });
      return;
    }

    const pairKey = getPairKey(source, target);
    if (existingKeys.has(pairKey)) {
      duplicateEntries.push(entry);
      return;
    }

    existingKeys.add(pairKey);
    pairsToAdd.push(`${source} | ${target}`);
  });

  if (!pairsToAdd.length) {
    return { kind: 'error', error: 'noNewPairs' };
  }

  return {
    kind: 'success',
    pairsToAdd,
    invalidEntries,
    duplicateEntries,
  };
};

const SERIES_METADATA_SEED_FIELDS: (keyof AggregatedData)[] = [
  'seriesDescription',
  'genre',
  'tropes',
  'keywords',
  'intendedAudience',
];

const hasMeaningfulSeriesDefaults = (record?: SeriesMetadata | null): boolean => {
  if (!record) return false;
  if (hasValue(record.description)) return true;
  const defaults = (record.defaults || {}) as Record<string, unknown>;
  return SERIES_METADATA_SEED_FIELDS.some(field => hasValue((defaults as Record<string, unknown>)[field]));
};

const PageContainer = styled.div``;

const BookCover = styled.div<{ src?: string }>`
  width: 70px;
  height: 110px;
  border-radius: 8px;
  background: ${({ src }) =>
    src
      ? `center/cover no-repeat url(${src})`
      : 'linear-gradient(135deg, var(--surface-base), var(--surface-muted))'};
  border: 1px solid var(--border-subtle);
  flex-shrink: 0;
  cursor: pointer;
`;

type SeriesSectionProps = {
  kicker?: string;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  tourId?: string;
};

const SeriesSection: React.FC<SeriesSectionProps> = ({
  kicker,
  title,
  description,
  action,
  children,
  className,
  tourId,
}) => (
  <section className={`series-management-section${className ? ` ${className}` : ''}`} data-tour-id={tourId}>
    <div className="series-management-section-header">
      <div>
        {kicker && <div className="series-management-kicker">{kicker}</div>}
        <h2 className="series-management-section-title">{title}</h2>
        {description && <div className="series-management-section-description">{description}</div>}
      </div>
      {action && <div className="series-management-section-action">{action}</div>}
    </div>
    <div className="series-management-section-body">{children}</div>
  </section>
);

type SeriesFieldProps = {
  label: React.ReactNode;
  helper?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

const SeriesField: React.FC<SeriesFieldProps> = ({ label, helper, children, className }) => (
  <div className={`series-management-field${className ? ` ${className}` : ''}`}>
    <div className="series-management-field-label">{label}</div>
    {children}
    {helper && <div className="series-management-field-helper">{helper}</div>}
  </div>
);

const SeriesModalTitle: React.FC<{ kicker?: string; title: string; description?: React.ReactNode }> = ({
  kicker,
  title,
  description,
}) => (
  <div className="series-management-modal-title">
    {kicker && <div className="series-management-kicker">{kicker}</div>}
    <div className="series-management-modal-heading">{title}</div>
    {description && <div className="series-management-modal-description">{description}</div>}
  </div>
);

interface SeriesManagementPageProps {
  testOverrides?: {
    loading?: boolean;
    seriesRecord?: SeriesMetadata | null;
    translateRules?: TranslateRules;
    selectedPair?: string;
    books?: SeriesBook[];
    aggregated?: AggregatedData;
    translationBookDetail?: SeriesTranslationBookDetail | null;
    descriptionTargetLanguage?: string;
    descriptionModel?: string;
  };
}

const SeriesManagementPage: React.FC<SeriesManagementPageProps> = ({ testOverrides }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = getAuth();
  const tutorialSandbox = useTutorialSandbox();
  const tutorialFixturePayload =
    tutorialSandbox.enabled && tutorialSandbox.mode === 'fixture' && tutorialSandbox.fixturePayload
      ? tutorialSandbox.fixturePayload
      : null;
  const isTutorialFixtureMode = Boolean(tutorialFixturePayload?.seriesRecord);
  const tutorialSeriesOverrides = useMemo(() => {
    if (!isTutorialFixtureMode || !tutorialFixturePayload) return null;
    return {
      loading: false,
      seriesRecord: tutorialFixturePayload.seriesRecord as SeriesMetadata,
      translateRules: tutorialFixturePayload.translateRules as TranslateRules,
      selectedPair: String(tutorialFixturePayload.selectedPair || 'English-German'),
      books: Array.isArray(tutorialFixturePayload.books)
        ? (tutorialFixturePayload.books as SeriesBook[])
        : [],
      aggregated: tutorialFixturePayload.aggregated as AggregatedData,
    };
  }, [isTutorialFixtureMode, tutorialFixturePayload]);
  const activeOverrides: SeriesManagementPageProps['testOverrides'] =
    testOverrides ?? tutorialSeriesOverrides ?? undefined;
  const tourUserId = isTutorialFixtureMode ? 'tutorial-user' : auth.currentUser?.uid ?? null;

  const incomingSeriesId = searchParams.get('seriesId') || '';
  const incomingSeriesName = searchParams.get('seriesName') || '';
  const incomingFocus = searchParams.get('focus') || '';

  const [seriesRecord, setSeriesRecord] = useState<SeriesMetadata | null>(null);
  const [books, setBooks] = useState<SeriesBook[]>([]);
  const [translationBookDetail, setTranslationBookDetail] = useState<SeriesTranslationBookDetail | null>(
    testOverrides?.translationBookDetail ?? null
  );
  const [aggregated, setAggregated] = useState<AggregatedData>(EMPTY_AGGREGATED);
  const [formState, setFormState] = useState<AggregatedData>({
    name: incomingSeriesName,
    genre: [],
    tropes: [],
    keywords: [],
    intendedAudience: [],
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const savedFormState = useRef(formState);
  useEffect(() => {
    if (!hasUnsavedChanges) savedFormState.current = formState;
  }, [formState, hasUnsavedChanges]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [isRefreshModalVisible, setIsRefreshModalVisible] = useState(false);
  const [selectedRefreshFields, setSelectedRefreshFields] = useState<RefreshableField[]>([...REFRESHABLE_FIELDS]);
  const [translateRules, setTranslateRules] = useState<TranslateRules>({ alwaysTranslate: {}, neverTranslate: {} });
  const [selectedPair, setSelectedPair] = useState<string>('');
  const [newAlwaysSource, setNewAlwaysSource] = useState('');
  const [newAlwaysTarget, setNewAlwaysTarget] = useState('');
  const [bulkAlwaysInput, setBulkAlwaysInput] = useState('');
  const [alwaysSearch, setAlwaysSearch] = useState('');
  const [newNeverTerm, setNewNeverTerm] = useState('');
  const [neverSearch, setNeverSearch] = useState('');
  const [isAlwaysModalVisible, setIsAlwaysModalVisible] = useState(false);
  const [isNeverModalVisible, setIsNeverModalVisible] = useState(false);
  const [editingAlwaysIndex, setEditingAlwaysIndex] = useState<number | null>(null);
  const [editingAlwaysSource, setEditingAlwaysSource] = useState('');
  const [editingAlwaysTarget, setEditingAlwaysTarget] = useState('');
  const [editingNeverIndex, setEditingNeverIndex] = useState<number | null>(null);
  const [editingNeverValue, setEditingNeverValue] = useState('');
  const [updatingRules, setUpdatingRules] = useState(false);
  const [isAudienceInfoModalVisible, setIsAudienceInfoModalVisible] = useState(false);
  const [descriptionTargetLanguage, setDescriptionTargetLanguage] = useState(
    testOverrides?.descriptionTargetLanguage || ''
  );
  const [descriptionModel, setDescriptionModel] = useState(testOverrides?.descriptionModel || '');
  const [descriptionQuotationStyle, setDescriptionQuotationStyle] = useState('');
  const [descriptionFrenchTone, setDescriptionFrenchTone] = useState('');
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [editingDescriptionTranslation, setEditingDescriptionTranslation] = useState(false);
  const [translatingDescription, setTranslatingDescription] = useState(false);
  const [savingDescriptionTranslation, setSavingDescriptionTranslation] = useState(false);
  const [descriptionSetupOpen, setDescriptionSetupOpen] = useState(false);
  const [descriptionTranslationError, setDescriptionTranslationError] = useState('');
  const [confirmDescriptionReplacement, setConfirmDescriptionReplacement] = useState(false);
  const [loadingContext, setLoadingContext] = useState<LoadingContext>('initial');
  const translationLanguages = useMemo(() => getAllLanguages(true), []);
  const languages = useMemo(() => translationLanguages.map(normalizeTargetLangKey), [translationLanguages]);
  const {
    flags: tourFlags,
    initialized: tourFlagsInitialized,
    markSeen: markTourSeen,
  } = useTourFlags(tourUserId, API_BASE, { storagePrefix: 'seriesTour' });
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const tourAutoTriggeredRef = useRef(false);
  const tourFallbackRef = useRef<HTMLElement | null>(null);
  const hasSeenSeriesTour = !!tourFlags.seriesManagementTourSeen;

  const resolvedName = useMemo(() => formState.name || incomingSeriesName, [formState.name, incomingSeriesName]);
  const translationContextBook = useMemo<SeriesTranslationBookDetail | null>(() => {
    if (translationBookDetail) return translationBookDetail;
    const bookWithLanguage = books.find(book => cleanString((book as SeriesTranslationBookDetail).language));
    return (bookWithLanguage as SeriesTranslationBookDetail | undefined) || null;
  }, [books, translationBookDetail]);
  const descriptionSourceLanguage = cleanString(translationContextBook?.language);
  const savedSeriesDescription = cleanString(seriesRecord?.description);
  const savedSeriesDescriptionWordCount = useMemo(
    () => countWordsIncludingMarkup(savedSeriesDescription),
    [savedSeriesDescription]
  );
  const currentDescriptionTranslation = useMemo(
    () => resolveSeriesDescriptionTranslation(
      seriesRecord?.translations,
      descriptionSourceLanguage,
      descriptionTargetLanguage,
    ),
    [descriptionSourceLanguage, descriptionTargetLanguage, seriesRecord?.translations]
  );
  const hasCurrentDescriptionTranslation = Boolean(currentDescriptionTranslation.trim());
  const descriptionTargetOptions = useMemo(
    () => translationLanguages.filter(
      language => language.trim().toLocaleLowerCase() !== descriptionSourceLanguage.toLocaleLowerCase()
    ),
    [descriptionSourceLanguage, translationLanguages]
  );
  const quotationOptions = getQuotationStyle(descriptionTargetLanguage)?.options || [];
  const seriesDescriptionTooLong = savedSeriesDescriptionWordCount > MAX_BLURB_TRANSLATION_WORDS;
  const descriptionDraftWordCount = useMemo(
    () => countWordsIncludingMarkup(descriptionDraft),
    [descriptionDraft]
  );
  const descriptionTranslationBlockedReason = useMemo(() => {
    if (!savedSeriesDescription) return 'Add and save a source series description before translating it.';
    if (hasUnsavedChanges) return 'Save the pending series changes before translating the canonical description.';
    if (!translationContextBook?.book_guid) return 'Attach at least one book before translating this series description.';
    if (!descriptionSourceLanguage) return 'The source language could not be determined from the attached book.';
    if (seriesDescriptionTooLong) {
      return `Shorten the source description to ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words or fewer.`;
    }
    return '';
  }, [
    descriptionSourceLanguage,
    hasUnsavedChanges,
    savedSeriesDescription,
    seriesDescriptionTooLong,
    translationContextBook?.book_guid,
  ]);
  const getElement = useCallback((id: string) => {
    if (typeof document === 'undefined') return null;
    return document.querySelector(`[data-tour-id="${id}"]`) as HTMLElement | null;
  }, []);

  const resolveTourTarget = useCallback(
    (element?: HTMLElement | null, fallbackToBody: boolean = true): HTMLElement => {
      if (element) return element;
      if (typeof document !== 'undefined' && fallbackToBody && document.body) {
        return document.body;
      }
      if (!tourFallbackRef.current) {
        tourFallbackRef.current =
          typeof document !== 'undefined'
            ? document.createElement('div')
            : ({} as HTMLElement);
      }
      return tourFallbackRef.current;
    },
    []
  );

  const getPreferredTarget = useCallback(
    (ids: string[], fallbackToBody: boolean = true): HTMLElement => {
      for (const id of ids) {
        const el = getElement(id);
        if (el) return el;
      }
      return resolveTourTarget(null, fallbackToBody);
    },
    [getElement, resolveTourTarget]
  );

  const renderTourPanel = useCallback(
    (content: React.ReactNode) => renderAppTourPanel(content),
    []
  );
  const handleTourClose = useCallback(() => {
    setIsTourOpen(false);
    void markTourSeen('seriesManagementTourSeen');
  }, [markTourSeen]);

  const handleTourChange = useCallback((nextStep: number) => setTourStep(nextStep), []);

  const handleTourStart = useCallback(() => {
    tourAutoTriggeredRef.current = true;
    setTourStep(0);
    setIsTourOpen(true);
  }, []);

  const persistAggregatedDefaults = useCallback(
    async (
      record: SeriesMetadata,
      aggregatedDefaults: AggregatedData | null,
      overwriteExisting: boolean,
      headers: Record<string, string>,
      fieldsToOverwrite?: MetadataRefreshField[]
    ): Promise<SeriesMetadata | null> => {
      if (!auth.currentUser || !record?.seriesId || !aggregatedDefaults) return null;
      const existingDefaults = (record.defaults || {}) as Record<string, any>;
      const mergedDefaults: Record<string, any> = { ...existingDefaults };
      const targetedFields = Array.isArray(fieldsToOverwrite);

      const shouldApplyField = (field: keyof AggregatedData) => {
        if (!targetedFields) return true;
        return fieldsToOverwrite?.includes(field as MetadataRefreshField);
      };

      const applyField = (field: keyof AggregatedData) => {
        if (!shouldApplyField(field)) return;
        const nextValue = aggregatedDefaults[field];
        if (!hasValue(nextValue)) return;
        if (overwriteExisting || !hasValue(existingDefaults[field])) {
          mergedDefaults[field] = nextValue;
        }
      };

      applyField('author');
      applyField('genre');
      applyField('tropes');
      applyField('keywords');
      applyField('intendedAudience');

      const shouldUpdatePrimaryFields = !targetedFields;
      const nextAuthor =
        shouldUpdatePrimaryFields && (overwriteExisting || !hasValue(record.author))
          ? aggregatedDefaults.author ?? record.author
          : record.author;
      const nextDescription =
        shouldUpdatePrimaryFields && (overwriteExisting || !hasValue(record.description))
          ? aggregatedDefaults.seriesDescription ?? record.description ?? ''
          : record.description ?? '';
      const defaultsPayload = { ...mergedDefaults };
      if (shouldUpdatePrimaryFields) {
        defaultsPayload.author = nextAuthor ?? mergedDefaults.author;
      }

      try {
        const payload: Record<string, any> = {
          userId: auth.currentUser.uid,
          name: record.name,
          defaults: defaultsPayload,
          defaultsPopulated: true,
        };
        if (shouldUpdatePrimaryFields) {
          payload.description = nextDescription ?? '';
          payload.author = nextAuthor ?? '';
        }
        const { data } = await axios.patch<SeriesMetadata>(
          `${API_BASE}/series/${record.seriesId}`,
          payload,
          { headers }
        );
        return data;
      } catch (err) {
        console.warn('Failed to persist aggregated defaults to series record', err);
        return null;
      }
    },
    [auth.currentUser]
  );

  const persistAggregatedTranslateRules = useCallback(
    async (
      record: SeriesMetadata | null,
      aggregatedRules: TranslateRules | null,
      options: { overwriteExisting: boolean; headers: Record<string, string>; currentRules?: TranslateRules }
    ): Promise<TranslateRules | null> => {
      if (!auth.currentUser || !record?.seriesId || !aggregatedRules) return null;
      if (!translateRulesHaveValues(aggregatedRules)) return null;

      const baseline = options.currentRules ?? translateRules;
      const nextRules: TranslateRules = {
        alwaysTranslate: { ...(baseline?.alwaysTranslate || {}) },
        neverTranslate: { ...(baseline?.neverTranslate || {}) },
      };
      let changed = false;

      const mergeBucket = (bucket: keyof TranslateRules, data: Record<string, string[]>) => {
        Object.entries(data).forEach(([pair, values]) => {
          if (!values || values.length === 0) return;
          const existing = nextRules[bucket][pair] ? [...nextRules[bucket][pair]] : [];
          if (options.overwriteExisting || existing.length === 0) {
            const deduped = Array.from(new Set(values));
            const identical =
              existing.length === deduped.length && existing.every((value, index) => value === deduped[index]);
            if (!identical) {
              nextRules[bucket][pair] = deduped;
              changed = true;
            }
            return;
          }
          const merged = [...existing];
          const seen = new Set(existing);
          values.forEach(value => {
            if (!seen.has(value)) {
              merged.push(value);
              seen.add(value);
            }
          });
          const appended = merged.length !== existing.length;
          if (appended) {
            nextRules[bucket][pair] = merged;
            changed = true;
          }
        });
      };

      mergeBucket('alwaysTranslate', aggregatedRules.alwaysTranslate);
      mergeBucket('neverTranslate', aggregatedRules.neverTranslate);

      if (!changed) {
        return null;
      }

      await axios.post(
        `${API_BASE}/series/${record.seriesId}/translate-rules`,
        {
          userId: auth.currentUser.uid,
          alwaysTranslate: nextRules.alwaysTranslate,
          neverTranslate: nextRules.neverTranslate,
        },
        { headers: options.headers }
      );
      setTranslateRules(nextRules);
      return nextRules;
    },
    [auth.currentUser, setTranslateRules, translateRules]
  );

  const fetchBooksForSeries = useCallback(
    async (seriesId?: string, seriesName?: string, headers?: Record<string, string>) => {
      if (!auth.currentUser) return [];
      const seen = new Set<string>();
      const result: SeriesBook[] = [];

      if (seriesId) {
        try {
          const { data } = await axios.get<SeriesBook[]>(`${API_BASE}/series/${seriesId}/books`, {
            params: { user_id: auth.currentUser.uid },
            headers,
          });
          (data || []).forEach(item => {
            if (item.book_guid && !seen.has(item.book_guid)) {
              seen.add(item.book_guid);
              result.push(item);
            }
          });
        } catch (error) {
          console.warn('Failed to load books by series id', error);
        }
      }

      if (seriesName) {
        try {
          const { data } = await axios.get<SeriesBook[]>(
            `${API_BASE}/uploadedbooks/series/${encodeURIComponent(seriesName)}`,
            {
              params: { user_id: auth.currentUser.uid },
              headers,
            }
          );
          (data || []).forEach(item => {
            if (item.book_guid && !seen.has(item.book_guid)) {
              seen.add(item.book_guid);
              result.push(item);
            }
          });
        } catch (error) {
          console.warn('Failed to load books by series name', error);
        }
      }

      return result;
    },
    [auth.currentUser]
  );

  const fetchBookDetails = useCallback(
    async (bookGuid: string, headers?: Record<string, string>) => {
      const { data } = await requestWithRetry(() =>
        axios.get(`${API_BASE}/uploadedbooks/book/${bookGuid}?slim=true`, { headers })
      );
      // Detail responses may omit the document ID; retain the attached book we requested.
      return { ...data, book_guid: bookGuid };
    },
    []
  );

  const aggregateFromBooks = useCallback(aggregateDefaultsFromBooks, []);

const resolveSeriesRecord = useCallback(
  async (
    headers: Record<string, string>,
    allowCreate: boolean
  ): Promise<{ record: SeriesMetadata | null; wasCreated: boolean }> => {
      if (!auth.currentUser) return { record: null, wasCreated: false };
      const userId = auth.currentUser.uid;
      let record: SeriesMetadata | null = null;
      let seriesId = incomingSeriesId.trim();
      const desiredName = normalizeSeriesInput(incomingSeriesName);

      if (seriesId) {
        try {
          const { data } = await axios.get<SeriesMetadata>(`${API_BASE}/series/${seriesId}`, {
            params: { user_id: userId },
            headers,
          });
          record = data;
        } catch (error) {
          // An explicit ID must never fall back to another same-name series or create a duplicate.
          throw error;
        }
      }

      if (!record && desiredName) {
        try {
          const { data } = await axios.get<SeriesMetadata[]>(`${API_BASE}/series`, {
            params: { user_id: userId },
            headers,
          });
          const matches = data.filter(
            item => item.name && item.name.toLowerCase() === desiredName.toLowerCase()
          );
          if (matches.length > 1) {
            throw new Error('More than one series has this name. Open the intended series from a linked book.');
          }
          const match = matches[0];
          if (match) {
            record = match;
            seriesId = match.seriesId;
          }
        } catch (error) {
          console.error('Failed to search for existing series by name', error);
          throw error;
        }
      }

      if (record || !allowCreate || !desiredName) {
        return { record, wasCreated: false };
      }

      // No series record yet—create one using aggregated defaults
      const discoveredBooks = await fetchBooksForSeries(undefined, desiredName, headers);
      const detailedBooks = await Promise.all(
        discoveredBooks.map(book => fetchBookDetails(book.book_guid, headers).catch(() => null))
      );
      const filteredBooks = detailedBooks.filter(Boolean);
      const aggregatedDefaults = aggregateFromBooks(filteredBooks);
      const aggregatedRules = aggregateTranslateRulesFromBooks(filteredBooks);

      try {
        const payload: any = {
          userId,
          name: normalizeSeriesInput(desiredName),
          creationKey: `implicit-name-v1:${desiredName.toLowerCase()}`,
          description: aggregatedDefaults.seriesDescription || '',
          author: aggregatedDefaults.author || '',
          defaultsPopulated: true,
          defaults: {
            author: aggregatedDefaults.author,
            genre: aggregatedDefaults.genre,
            tropes: aggregatedDefaults.tropes,
            keywords: aggregatedDefaults.keywords,
            intendedAudience: aggregatedDefaults.intendedAudience,
          },
        };
        if (translateRulesHaveValues(aggregatedRules)) {
          payload.alwaysTranslate = aggregatedRules.alwaysTranslate;
          payload.neverTranslate = aggregatedRules.neverTranslate;
        }
        const { data } = await axios.post<SeriesMetadata>(`${API_BASE}/series`, payload, { headers });
        const createdId = data.seriesId;

        // Attach all discovered books to the newly created series
        await Promise.all(
          discoveredBooks.map(book =>
            axios
              .post(
                `${API_BASE}/series/assign`,
                {
                  userId,
                  seriesId: createdId,
                  bookId: book.book_guid,
                  inheritance: {},
                  onlyIfUnassigned: true,
                },
                { headers }
              )
              .catch(err => {
                console.warn('Failed to attach book to new series', book.book_guid, err);
              })
          )
        );

        message.success('Created a series record from your existing books.');
        return { record: data, wasCreated: true };
      } catch (error) {
        console.error('Failed to create series record', error);
        message.error('Could not create a series record for this series.');
        return { record: null, wasCreated: false };
      }
    },
    [aggregateFromBooks, fetchBookDetails, fetchBooksForSeries, auth.currentUser, incomingSeriesId, incomingSeriesName]
  );

  const seriesTourSteps = useMemo<TourProps['steps']>(() => [
    {
      title: 'Why this page?',
      description: renderTourPanel(
        <>
          This workspace holds the defaults for every book in the series—those inherited fields appear locked on book pages, so set them here first.
        </>
      ),
      target: () => getPreferredTarget(['series-overview']),
      placement: 'bottom',
    },
    {
      title: 'Define your series',
      description: renderTourPanel(
        <>
          Set the series name and author here. These become shared defaults that book pages can
          display as locked series context.
        </>
      ),
      target: () => getPreferredTarget(['series-basics']),
      placement: 'right',
    },
    {
      title: 'Audience & tone',
      description: renderTourPanel(
        <>
          Use this column to describe the series and specify an intended audience. Individual books can still add extra detail, but these defaults keep the tone consistent.
        </>
      ),
      target: () => getPreferredTarget(['series-audience']),
      placement: 'left',
    },
    {
      title: 'Translate the description once',
      description: renderTourPanel(
        <>
          Create and review each localized series description here. One saved translation is shared with every attached book.
        </>
      ),
      target: () => getPreferredTarget(['series-description-translations']),
      placement: 'left',
    },
    {
      title: 'Shared tags',
      description: renderTourPanel(
        <>
          Genres, Tropes, and Keywords all accept comma-separated lists. The locked tags you see on book pages come from here, so you immediately know what’s already covered.
        </>
      ),
      target: () => getPreferredTarget(['series-tags']),
      placement: 'right',
    },
    {
      title: 'Glossary rules',
      description: renderTourPanel(
        <>
          Keep Always/Never Translate guidance in one place. These language-specific glossary rules cascade to every book and apply during translation.
        </>
      ),
      target: () => getPreferredTarget(['series-translate']),
      placement: 'left',
    },
    {
      title: 'Books in this series',
      description: renderTourPanel(
        <>
          Every attached book shows up here with quick links back to its metadata. Use this list to verify that your defaults match the actual catalog.
        </>
      ),
      target: () => getPreferredTarget(['series-books']),
      placement: 'top',
    },
    {
      title: 'Refresh from books',
      description: renderTourPanel(
        <>
          “Refresh from Books” pulls suggestions from attached titles and can overwrite series defaults, so review changes before saving.
        </>
      ),
      target: () => getPreferredTarget(['series-refresh-button']),
      placement: 'left',
    },
    {
      title: 'Save anytime',
      description: renderTourPanel(
        <>
          Don't forget to use the save icon to lock in your edits. Series defaults can flow into
          book metadata, while book-specific fields stay editable on each book.
        </>
      ),
      target: () => getPreferredTarget(['series-save-button', 'series-tour-trigger']),
      placement: 'left',
    },
  ], [getPreferredTarget, renderTourPanel]);

  const loadData = useCallback(
    async (
      allowCreate: boolean,
      options?: { pullFromBooks?: boolean; refreshFields?: RefreshableField[]; context?: LoadingContext }
    ) => {
      const pullFromBooks = options?.pullFromBooks ?? false;
      const refreshFields = options?.refreshFields;
      const metadataRefreshSelection = refreshFields?.filter(isMetadataRefreshField) ?? [];
      const translateRefreshSelection = refreshFields?.filter(isTranslateRefreshField) ?? [];
      const metadataFieldsToOverwrite = refreshFields ? metadataRefreshSelection : undefined;
      const translateSelectionSet = new Set<TranslateRefreshField>(
        refreshFields ? translateRefreshSelection : TRANSLATE_REFRESH_FIELDS
      );
      setLoadingContext(options?.context ?? null);
      const user = auth.currentUser;
      if (!user) {
        message.error('Please sign in to manage series metadata.');
        navigate('/manage-books');
        return;
      }
      setLoading(true);
      try {
        const headers = await getAuthHeaders(user);
        const resolved = await resolveSeriesRecord(headers, allowCreate);
        let record = resolved.record;
        const targetId = record?.seriesId || incomingSeriesId || '';
        let targetName = record?.name || incomingSeriesName;
        const hasSeededDefaults = hasMeaningfulSeriesDefaults(record);
        const defaultsLikelyMissing = !!record && record.defaultsPopulated && !hasSeededDefaults;
        const needsInitialDefaults = resolved.wasCreated || !record?.defaultsPopulated || defaultsLikelyMissing;
        const shouldAggregate = pullFromBooks || needsInitialDefaults || !record;
        let currentRulesSnapshot: TranslateRules = translateRules;

        const bookList = await fetchBooksForSeries(targetId, targetName, headers);
        setBooks(bookList);
        if (targetId) {
          try {
            const rulesResp = await axios.get(`${API_BASE}/series/${targetId}/translate-rules`, {
              params: { user_id: user.uid },
              headers,
            });
            const fetchedRules: TranslateRules = {
              alwaysTranslate: rulesResp.data?.alwaysTranslate || {},
              neverTranslate: rulesResp.data?.neverTranslate || {},
            };
            setTranslateRules(fetchedRules);
            currentRulesSnapshot = fetchedRules;
            const pairs = Object.keys(rulesResp.data?.alwaysTranslate || rulesResp.data?.neverTranslate || {});
            if (pairs.length > 0) {
              setSelectedPair(prev => prev || pairs[0]);
            }
          } catch (err) {
            console.warn('Failed to load series translate rules', err);
          }
        }

        let aggregatedDefaults: AggregatedData | null = null;
        let aggregatedRules: TranslateRules | null = null;
        let detailedBooks: Array<SeriesTranslationBookDetail | null> = [];
        if (shouldAggregate) {
          detailedBooks = await Promise.all(
            bookList.map(book => fetchBookDetails(book.book_guid, headers).catch(() => null))
          );
          const filteredBooks = detailedBooks.filter(Boolean) as SeriesTranslationBookDetail[];
          aggregatedDefaults = aggregateFromBooks(filteredBooks);
          aggregatedRules = aggregateTranslateRulesFromBooks(filteredBooks);
          setAggregated(aggregatedDefaults ?? { ...EMPTY_AGGREGATED });
          if (record && (needsInitialDefaults || pullFromBooks)) {
            const persisted = await persistAggregatedDefaults(
              record,
              aggregatedDefaults,
              pullFromBooks,
              headers,
              metadataFieldsToOverwrite
            );
            if (persisted) {
              record = persisted;
            }
            const shouldApplyAlwaysTranslate =
              needsInitialDefaults || !pullFromBooks || !refreshFields || translateSelectionSet.has('alwaysTranslate');
            const shouldApplyNeverTranslate =
              needsInitialDefaults || !pullFromBooks || !refreshFields || translateSelectionSet.has('neverTranslate');
            if (aggregatedRules && (shouldApplyAlwaysTranslate || shouldApplyNeverTranslate)) {
              const filteredRules: TranslateRules = { alwaysTranslate: {}, neverTranslate: {} };
              if (shouldApplyAlwaysTranslate) {
                filteredRules.alwaysTranslate = aggregatedRules.alwaysTranslate;
              }
              if (shouldApplyNeverTranslate) {
                filteredRules.neverTranslate = aggregatedRules.neverTranslate;
              }
              if (translateRulesHaveValues(filteredRules)) {
                const persistedRules = await persistAggregatedTranslateRules(record, filteredRules, {
                  overwriteExisting: pullFromBooks,
                  headers,
                  currentRules: currentRulesSnapshot,
                });
                if (persistedRules) {
                  currentRulesSnapshot = persistedRules;
                }
              }
            }
          }
        } else {
          setAggregated({ ...EMPTY_AGGREGATED });
          if (bookList[0]?.book_guid) {
            detailedBooks = [
              await fetchBookDetails(bookList[0].book_guid, headers).catch(() => null),
            ];
          }
        }
        const firstTranslationBook = detailedBooks.find(
          detail => detail && cleanString(detail.language)
        ) || detailedBooks.find(Boolean) || null;
        setTranslationBookDetail(firstTranslationBook as SeriesTranslationBookDetail | null);
        if (record) {
          setSeriesRecord(record);
        }

        targetName = record?.name || aggregatedDefaults?.name || incomingSeriesName;
        const defaults = record?.defaults || {};
        const normalizedDefaults = {
          genre: ensureStringArray((defaults as any).genre),
          tropes: ensureStringArray((defaults as any).tropes),
          keywords: ensureStringArray((defaults as any).keywords),
          intendedAudience: ensureStringArray((defaults as any).intendedAudience),
        };
        const aggregatedSafe = aggregatedDefaults ?? { ...EMPTY_AGGREGATED };
        if (record) {
          setFormState({
            name: record.name || targetName,
            seriesDescription: record.description || '',
            author: record.author || (defaults as any).author || '',
            genre: normalizedDefaults.genre,
            tropes: normalizedDefaults.tropes,
            keywords: normalizedDefaults.keywords,
            intendedAudience: normalizedDefaults.intendedAudience,
          });
        } else {
          setFormState({
            name: aggregatedDefaults?.name || targetName,
            seriesDescription: aggregatedDefaults?.seriesDescription || '',
            author: aggregatedDefaults?.author || '',
            genre: aggregatedSafe.genre || [],
            tropes: aggregatedSafe.tropes || [],
            keywords: aggregatedSafe.keywords || [],
            intendedAudience: aggregatedSafe.intendedAudience || [],
          });
        }
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Failed to load series data', error);
        message.error('Unable to load series information right now.');
      } finally {
        setLoading(false);
        setRefreshing(false);
        setLoadingContext(null);
      }
    },
    [
      aggregateFromBooks,
      auth.currentUser,
      fetchBookDetails,
      fetchBooksForSeries,
      incomingSeriesId,
      incomingSeriesName,
      navigate,
      persistAggregatedDefaults,
      resolveSeriesRecord,
      setHasUnsavedChanges,
    ]
  );

  useEffect(() => {
    if (!tourFlagsInitialized || hasSeenSeriesTour || loading || tourAutoTriggeredRef.current) return;
    tourAutoTriggeredRef.current = true;
    setTourStep(0);
    setIsTourOpen(true);
  }, [tourFlagsInitialized, hasSeenSeriesTour, loading]);

  useEffect(() => {
    if (loading || incomingFocus !== 'description-translations' || typeof document === 'undefined') return;
    const target = document.querySelector('[data-tour-id="series-description-translations"]');
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [incomingFocus, loading]);

  useEffect(() => {
    if (activeOverrides) {
      if (typeof activeOverrides.loading === 'boolean') {
        setLoading(activeOverrides.loading);
      }
      if (activeOverrides.seriesRecord !== undefined) {
        setSeriesRecord(activeOverrides.seriesRecord);
        if (activeOverrides.seriesRecord) {
          const defaults = activeOverrides.seriesRecord.defaults || {};
          setFormState({
            name: activeOverrides.seriesRecord.name || incomingSeriesName,
            seriesDescription: activeOverrides.seriesRecord.description || '',
            author: activeOverrides.seriesRecord.author || defaults.author || '',
            genre: ensureStringArray(defaults.genre),
            tropes: ensureStringArray(defaults.tropes),
            keywords: ensureStringArray(defaults.keywords),
            intendedAudience: ensureStringArray(defaults.intendedAudience),
          });
        }
      }
      if (activeOverrides.aggregated) {
        setAggregated(activeOverrides.aggregated);
      }
      if (activeOverrides.translateRules) {
        setTranslateRules(activeOverrides.translateRules);
      }
      if (typeof activeOverrides.selectedPair === 'string') {
        setSelectedPair(activeOverrides.selectedPair);
      }
      if (activeOverrides.books) {
        setBooks(activeOverrides.books);
      }
      if (activeOverrides.translationBookDetail !== undefined) {
        setTranslationBookDetail(activeOverrides.translationBookDetail);
      } else if (activeOverrides.books) {
        const detailedBook = activeOverrides.books.find(
          book => cleanString((book as SeriesTranslationBookDetail).language)
        );
        setTranslationBookDetail((detailedBook as SeriesTranslationBookDetail | undefined) || null);
      }
      if (typeof activeOverrides.descriptionTargetLanguage === 'string') {
        setDescriptionTargetLanguage(activeOverrides.descriptionTargetLanguage);
      }
      if (typeof activeOverrides.descriptionModel === 'string') {
        setDescriptionModel(activeOverrides.descriptionModel);
      }
      setHasUnsavedChanges(false);
    }
  }, [activeOverrides, incomingSeriesName]);

  useEffect(() => {
    if (activeOverrides) return;
    loadData(true, { context: 'initial' });
  }, [activeOverrides, loadData]);

  useEffect(() => {
    if (isAlwaysModalVisible) {
      setNewAlwaysSource('');
      setNewAlwaysTarget('');
      setAlwaysSearch('');
      setEditingAlwaysIndex(null);
      setEditingAlwaysSource('');
      setEditingAlwaysTarget('');
    }
  }, [isAlwaysModalVisible, selectedPair]);

  useEffect(() => {
    if (isNeverModalVisible) {
      setNewNeverTerm('');
      setNeverSearch('');
      setEditingNeverIndex(null);
      setEditingNeverValue('');
    }
  }, [isNeverModalVisible, selectedPair]);

  useEffect(() => {
    setDescriptionQuotationStyle(getDefaultQuotationStyle(descriptionTargetLanguage));
    setDescriptionFrenchTone(descriptionTargetLanguage === 'French' ? 'AI Detect' : '');
    setEditingDescriptionTranslation(false);
    setConfirmDescriptionReplacement(false);
  }, [descriptionTargetLanguage]);

  useEffect(() => {
    if (!editingDescriptionTranslation) {
      setDescriptionDraft(currentDescriptionTranslation);
    }
  }, [currentDescriptionTranslation, editingDescriptionTranslation]);

  const handleSave = async () => {
    if (!auth.currentUser || saving) return;
    const targetSeriesId = seriesRecord?.seriesId || incomingSeriesId.trim();
    if (!targetSeriesId) {
      message.error('Series record not ready yet. Refresh from books to retry loading it.');
      return;
    }
    if (!normalizeSeriesInput(formState.name)) {
      message.warning('Enter a series name before saving.');
      return;
    }
    setSaving(true);
    try {
      const headers = await getAuthHeaders(auth.currentUser);
      // Send only edits: unloaded fields must not overwrite existing canonical metadata.
      const changed = (field: keyof AggregatedData) =>
        JSON.stringify(formState[field]) !== JSON.stringify(savedFormState.current[field]);
      const payload: Record<string, unknown> = { userId: auth.currentUser.uid };
      if (changed('name')) payload.name = normalizeSeriesInput(formState.name);
      if (changed('seriesDescription')) payload.description = formState.seriesDescription?.trim() ?? '';
      if (changed('author')) payload.author = formState.author ?? '';
      const defaults: Record<string, unknown> = {};
      for (const field of ['author', 'genre', 'tropes', 'keywords', 'intendedAudience'] as const) {
        if (changed(field)) defaults[field] = formState[field];
      }
      if (Object.keys(defaults).length) payload.defaults = defaults;
      await axios.patch(`${API_BASE}/series/${targetSeriesId}`, payload, { headers });
      message.success('Series details saved');
      setHasUnsavedChanges(false);
      await loadData(false);
    } catch (error) {
      console.error('Saving series failed', error);
      message.error(getApiErrorMessage(error, 'Unable to save series details. Your changes are still pending; try saving again.'));
    } finally {
      setSaving(false);
    }
  };

  const updateLocalDescriptionTranslation = useCallback((value: string) => {
    if (!descriptionSourceLanguage || !descriptionTargetLanguage) return;
    const pairKey = `${descriptionSourceLanguage}-${descriptionTargetLanguage}`;
    setSeriesRecord(previous => {
      if (!previous) return previous;
      return {
        ...previous,
        translations: {
          ...(previous.translations || {}),
          seriesDescription: {
            ...(previous.translations?.seriesDescription || {}),
            [pairKey]: value,
          },
        },
      };
    });
  }, [descriptionSourceLanguage, descriptionTargetLanguage]);

  const runDescriptionTranslation = async (overwrite: boolean) => {
    const user = auth.currentUser;
    if (!user || !translationContextBook?.book_guid || !descriptionTargetLanguage) return;
    if (descriptionTranslationBlockedReason) {
      message.warning(descriptionTranslationBlockedReason);
      return;
    }

    if (translatingDescription) return;
    setDescriptionTranslationError('');
    setDescriptionSetupOpen(false);
    setConfirmDescriptionReplacement(false);
    setTranslatingDescription(true);
    try {
      const response = await axios.post(
        `${API_BASE}/translate-series-description/`,
        {
          userId: user.uid,
          bookId: translationContextBook.book_guid,
          sourceLang: descriptionSourceLanguage,
          targetLang: descriptionTargetLanguage,
          model: descriptionModel,
          genre: (formState.genre || translationContextBook.genre || translationContextBook.genres || []).join(', '),
          intendedAudience: formState.intendedAudience || translationContextBook.intendedAudience || [],
          quotationStyle: descriptionQuotationStyle,
          tone: descriptionTargetLanguage === 'French' ? descriptionFrenchTone : '',
          overwriteFlag: overwrite,
        },
        { headers: await getAuthHeaders(user) }
      );
      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message || 'Unable to translate the series description.');
      }
      const translatedValue = String(response.data.translated_series_description || '').trim();
      if (!translatedValue) {
        throw new Error('The translation service returned an empty series description.');
      }
      updateLocalDescriptionTranslation(translatedValue);
      setDescriptionDraft(translatedValue);
      setEditingDescriptionTranslation(false);
      setConfirmDescriptionReplacement(false);
      setDescriptionSetupOpen(false);
      const chargedCredits = Number(response.data?.chargedCredits || 0);
      message.success(
        chargedCredits > 0
          ? `Series description replaced (${chargedCredits.toLocaleString()} credits).`
          : 'Series description translated and shared with linked books.'
      );
    } catch (error) {
      setDescriptionSetupOpen(true);
      const detail = (error as any)?.response?.data?.detail;
      if (!overwrite && detail?.errorKind === 'METADATA_OVERWRITE_REQUIRED') {
        setConfirmDescriptionReplacement(true);
        return;
      }
      console.error('Series description translation failed', error);
      setConfirmDescriptionReplacement(false);
      setDescriptionTranslationError(getApiErrorMessage(error, 'Unable to translate the series description.'));
    } finally {
      setTranslatingDescription(false);
    }
  };

  const handleDescriptionTranslateClick = () => {
    if (!descriptionTargetLanguage) {
      message.info('Choose a target language first.');
      return;
    }
    if (hasCurrentDescriptionTranslation) {
      setConfirmDescriptionReplacement(true);
      return;
    }
    void runDescriptionTranslation(false);
  };

  const handleSaveDescriptionTranslation = async () => {
    const user = auth.currentUser;
    const translatedValue = descriptionDraft.trim();
    if (!user || !translationContextBook?.book_guid || !descriptionTargetLanguage) return;
    if (hasUnsavedChanges) {
      message.warning('Save the pending series changes before editing a shared translation.');
      return;
    }
    if (!translatedValue) {
      message.warning('Enter a translated series description before saving.');
      return;
    }
    const translatedWordCount = countWordsIncludingMarkup(translatedValue);
    if (translatedWordCount > MAX_BLURB_TRANSLATION_WORDS) {
      message.error(
        `Series description must be ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words or fewer. Current count: ${translatedWordCount.toLocaleString()} words.`,
        8
      );
      return;
    }

    setSavingDescriptionTranslation(true);
    try {
      const response = await axios.post(
        `${API_BASE}/update-translation/`,
        {
          userId: user.uid,
          bookId: translationContextBook.book_guid,
          field: 'seriesDescription',
          value: translatedValue,
          sourceLang: descriptionSourceLanguage,
          targetLang: descriptionTargetLanguage,
          overwriteFlag: true,
        },
        { headers: await getAuthHeaders(user) }
      );
      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message || 'Unable to save the series description translation.');
      }
      updateLocalDescriptionTranslation(translatedValue);
      setDescriptionDraft(translatedValue);
      setEditingDescriptionTranslation(false);
      message.success('Series description translation saved for all linked books.');
    } catch (error) {
      console.error('Saving series description translation failed', error);
      message.error(getApiErrorMessage(error, 'Unable to save the series description translation.'), 10);
    } finally {
      setSavingDescriptionTranslation(false);
    }
  };

  const handleCopyDescriptionTranslation = async () => {
    if (!currentDescriptionTranslation) return;
    try {
      await navigator.clipboard.writeText(currentDescriptionTranslation);
      message.success('Series description translation copied.');
    } catch (error) {
      console.error('Copying series description translation failed', error);
      message.error('Unable to copy the series description translation.');
    }
  };

  const applySuggestionsToEmptyFields = () => {
    setFormState(prev => fillEmptyFieldsWithSuggestions(prev, aggregated));
    setHasUnsavedChanges(true);
    message.success('Filled empty fields with suggestions from your books.');
  };

  const handleRefresh = async (fields: RefreshableField[]) => {
    setRefreshing(true);
    await loadData(false, { pullFromBooks: true, refreshFields: fields, context: 'refresh' });
  };

  const saveTranslateRules = async (
    nextRules: TranslateRules,
    options?: { successMessage?: string | null }
  ): Promise<boolean> => {
    if (!seriesRecord?.seriesId || !auth.currentUser) return false;
    setUpdatingRules(true);
    try {
      await axios.post(
        `${API_BASE}/series/${seriesRecord.seriesId}/translate-rules`,
        {
          userId: auth.currentUser.uid,
          alwaysTranslate: nextRules.alwaysTranslate,
          neverTranslate: nextRules.neverTranslate,
        },
        { headers: await getAuthHeaders(auth.currentUser) }
      );
      setTranslateRules(nextRules);
      const successMessage = options?.successMessage;
      if (successMessage !== null) {
        message.success(successMessage ?? 'Translate rules saved');
      }
      return true;
    } catch (err) {
      console.error('Failed to save translate rules', err);
      const detail = (err as any)?.response?.data?.detail;
      message.error(
        (typeof detail === 'string' ? detail : detail?.message) || 'Unable to save translate rules'
      );
      return false;
    } finally {
      setUpdatingRules(false);
    }
  };

  const closeAlwaysModal = () => {
    setIsAlwaysModalVisible(false);
    setEditingAlwaysIndex(null);
    setEditingAlwaysSource('');
    setEditingAlwaysTarget('');
    setNewAlwaysSource('');
    setNewAlwaysTarget('');
    setBulkAlwaysInput('');
  };

  const closeNeverModal = () => {
    setIsNeverModalVisible(false);
    setEditingNeverIndex(null);
    setEditingNeverValue('');
    setNewNeverTerm('');
  };

  const handleAddAlwaysPair = () => {
    if (!selectedPair) return;
    const existingList = [...(translateRules.alwaysTranslate[selectedPair] ?? [])];
    const trimmedSource = newAlwaysSource.trim();
    const neverTermSet = getNeverTermSet(translateRules.neverTranslate[selectedPair] ?? []);
    if (trimmedSource && neverTermSet.has(normalizeTerm(trimmedSource))) {
      message.warning('That source term already exists in the Never Translate glossary.');
      return;
    }
    const result = buildNextAlwaysPairs(existingList, newAlwaysSource, newAlwaysTarget);

    if (result.kind === 'error') {
      const messageMap: Record<AlwaysPairError, { text: string; type: 'error' | 'warning' }> = {
        missing: {
          text: 'Please enter both source and target words or phrases.',
          type: 'error',
        },
        identical: {
          text: 'Source and target must be different. Add identical terms to the Never Translate glossary instead.',
          type: 'error',
        },
        invalid: {
          text: 'Please enter valid words or phrases (letters, numbers, spaces, and common punctuation are allowed).',
          type: 'error',
        },
        duplicate: {
          text: 'That pair already exists.',
          type: 'warning',
        },
      };
      const { text, type } = messageMap[result.error];
      message[type](text);
      return;
    }

    const nextRules: TranslateRules = {
      alwaysTranslate: { ...translateRules.alwaysTranslate },
      neverTranslate: { ...translateRules.neverTranslate },
    };
    nextRules.alwaysTranslate[selectedPair] = result.list;
    void saveTranslateRules(nextRules, { successMessage: null });
    setNewAlwaysSource('');
    setNewAlwaysTarget('');
  };

  const handleSaveAlwaysEdit = () => {
    if (!selectedPair || editingAlwaysIndex === null) return;
    const source = editingAlwaysSource.trim();
    const target = editingAlwaysTarget.trim();
    if (!source || !target) {
      message.error('Please enter both source and target words or phrases.');
      return;
    }
    const previousParts = ((translateRules.alwaysTranslate[selectedPair] || [])[editingAlwaysIndex] || '').split('|').map(value => value.trim());
    if (source === previousParts[0] && target === previousParts[1]) {
      setEditingAlwaysIndex(null);
      return;
    }
    if (areTermsEquivalent(source, target)) {
      message.error('Source and target must be different. Add identical terms to the Never Translate glossary instead.');
      return;
    }
    const neverTermSet = getNeverTermSet(translateRules.neverTranslate[selectedPair] ?? []);
    if (neverTermSet.has(normalizeTerm(source))) {
      message.warning('That source term already exists in the Never Translate glossary.');
      return;
    }
    if (!isValidTerm(source) || !isValidTerm(target)) {
      message.error(
        'Please enter valid words or phrases (letters, numbers, spaces, and common punctuation are allowed).'
      );
      return;
    }

    const normalizedKey = getPairKey(source, target);
    const duplicateIndex = (translateRules.alwaysTranslate[selectedPair] ?? []).findIndex((pair, idx) => {
      if (idx === editingAlwaysIndex) return false;
      const [existingSource = '', existingTarget = ''] = pair.split('|').map(part => part.trim());
      return getPairKey(existingSource, existingTarget) === normalizedKey;
    });
    if (duplicateIndex !== -1) {
      message.warning('Another pair already has that source/target.');
      return;
    }

    const nextRules: TranslateRules = {
      alwaysTranslate: { ...translateRules.alwaysTranslate },
      neverTranslate: { ...translateRules.neverTranslate },
    };
    const list = nextRules.alwaysTranslate[selectedPair] ? [...nextRules.alwaysTranslate[selectedPair]] : [];
    list[editingAlwaysIndex] = `${source} | ${target}`;
    nextRules.alwaysTranslate[selectedPair] = list;
    void saveTranslateRules(nextRules, { successMessage: null });
    setEditingAlwaysIndex(null);
  };

  const beginEditingAlwaysPair = (index: number) => {
    if (!selectedPair) return;
    const term = translateRules.alwaysTranslate[selectedPair]?.[index] || '';
    const [source = '', target = ''] = term.split('|').map(v => v.trim());
    setEditingAlwaysIndex(index);
    setEditingAlwaysSource(source);
    setEditingAlwaysTarget(target);
  };

  const handleRemoveAlwaysPair = (index: number) => {
    if (!selectedPair) return;
    const nextRules: TranslateRules = {
      alwaysTranslate: { ...translateRules.alwaysTranslate },
      neverTranslate: { ...translateRules.neverTranslate },
    };
    const list = nextRules.alwaysTranslate[selectedPair] ? [...nextRules.alwaysTranslate[selectedPair]] : [];
    list.splice(index, 1);
    nextRules.alwaysTranslate[selectedPair] = list;
    void saveTranslateRules(nextRules, { successMessage: null });
    setEditingAlwaysIndex(null);
  };

  const handleBulkAlwaysImport = async () => {
    if (!selectedPair) return;
    const existingList = [...(translateRules.alwaysTranslate[selectedPair] ?? [])];
    const conflictSources = getNeverTermSet(translateRules.neverTranslate[selectedPair] ?? []);
    const result = parseBulkAlwaysImport(existingList, bulkAlwaysInput, { conflictSources });

    if (result.kind === 'error') {
      const errorMessages: Record<BulkImportError, { text: string; type: 'error' | 'warning' }> = {
        emptyInput: { text: 'Enter one or more pairs before importing.', type: 'error' },
        noEntries: {
          text: 'No valid pairs detected. Use the format "source ~ target | source2 ~ target2" or separate pairs with new lines.',
          type: 'error',
        },
        noNewPairs: { text: 'No new valid pairs to import.', type: 'warning' },
      };
      const { text, type } = errorMessages[result.error];
      message[type](text);
      return;
    }

    const { pairsToAdd, invalidEntries, duplicateEntries } = result;
    const nextRules: TranslateRules = {
      alwaysTranslate: {
        ...translateRules.alwaysTranslate,
        [selectedPair]: [...existingList, ...pairsToAdd],
      },
      neverTranslate: { ...translateRules.neverTranslate },
    };

    const saved = await saveTranslateRules(nextRules, { successMessage: null });
    if (!saved) return;

    setBulkAlwaysInput('');
    message.success(`Imported ${pairsToAdd.length} new pair${pairsToAdd.length > 1 ? 's' : ''}.`);
    if (invalidEntries.length) {
      const details = invalidEntries.map(({ entry, reason }) => `"${entry}" (${reason})`).join('; ');
      message.warning(
        `Skipped ${invalidEntries.length} invalid entr${invalidEntries.length > 1 ? 'ies' : 'y'}: ${details}`
      );
    }
    if (duplicateEntries.length) {
      const details = duplicateEntries.map(entry => `"${entry}"`).join('; ');
      message.info(`Ignored ${duplicateEntries.length} duplicate entr${duplicateEntries.length > 1 ? 'ies' : 'y'}: ${details}`);
    }
  };

  const currentAlwaysPairs = selectedPair ? translateRules.alwaysTranslate[selectedPair] ?? [] : [];
  const formatAlwaysPair = (pair: string) => {
    const [source = '', target = ''] = pair.split('|').map(value => value.trim());
    return `${source} ~ ${target}`;
  };

  const handleCopyAlwaysPairs = async (format: 'readable' | 'bulk') => {
    if (!currentAlwaysPairs.length) {
      message.info('No pairs to copy yet.');
      return;
    }

    const formatted = currentAlwaysPairs.map(formatAlwaysPair);
    const text = format === 'readable' ? formatted.join('\n') : formatted.join(' | ');

    try {
      await navigator.clipboard.writeText(text);
      message.success(format === 'readable' ? 'Copied human-readable list.' : 'Copied bulk import string.');
    } catch (err) {
      console.error('Clipboard copy failed', err);
      message.error('Unable to copy to clipboard.');
    }
  };

  const currentNeverTerms = selectedPair ? translateRules.neverTranslate[selectedPair] ?? [] : [];
  const alwaysPreviewPairs = currentAlwaysPairs.slice(0, GLOSSARY_PREVIEW_LIMIT);
  const neverPreviewTerms = currentNeverTerms.slice(0, GLOSSARY_PREVIEW_LIMIT);
  const alwaysRemainingCount = Math.max(0, currentAlwaysPairs.length - alwaysPreviewPairs.length);
  const neverRemainingCount = Math.max(0, currentNeverTerms.length - neverPreviewTerms.length);
  const filteredAlwaysPairs = useMemo(
    () =>
      currentAlwaysPairs
        .map((term, index) => {
          const [source = '', target = ''] = term.split('|').map(value => value.trim());
          return { index, source, target, term };
        })
        .filter(({ source, target }) => {
          const query = alwaysSearch.trim().toLocaleLowerCase();
          if (!query) return true;
          return source.toLocaleLowerCase().includes(query) || target.toLocaleLowerCase().includes(query);
        }),
    [alwaysSearch, currentAlwaysPairs]
  );
  const filteredNeverTerms = useMemo(
    () =>
      currentNeverTerms
        .map((term, index) => ({ index, term }))
        .filter(({ term }) => {
          const query = neverSearch.trim().toLocaleLowerCase();
          if (!query) return true;
          return term.toLocaleLowerCase().includes(query);
        }),
    [currentNeverTerms, neverSearch]
  );

  const handleCopyNeverTerms = async (format: 'readable' | 'pipe') => {
    if (!currentNeverTerms.length) {
      message.info('No terms to copy yet.');
      return;
    }

    const text =
      format === 'readable'
        ? currentNeverTerms.join('\n')
        : currentNeverTerms.map(term => term.trim()).join(' | ');

    try {
      await navigator.clipboard.writeText(text);
      message.success(format === 'readable' ? 'Copied list to clipboard.' : 'Copied import string to clipboard.');
    } catch (err) {
      console.error('Clipboard copy failed', err);
      message.error('Unable to copy to clipboard.');
    }
  };

  const applyNeverTermImport = async (
    input: string,
    emptyMessage: string,
    clearInput: () => void
  ) => {
    if (!selectedPair) return;
    const entries = splitGlossaryTerms(input);
    if (!entries.length) {
      message.error(emptyMessage);
      return;
    }

    const existingList = [...(translateRules.neverTranslate[selectedPair] ?? [])];
    const existingKeys = new Set(existingList.map(term => normalizeTerm(term)));
    const conflictAlwaysSourceSet = getAlwaysSourceSet(translateRules.alwaysTranslate[selectedPair] ?? []);
    const validEntries: string[] = [];
    const invalidEntries: Array<{ entry: string; reason: string }> = [];
    const duplicateEntries: string[] = [];

    entries.forEach(entry => {
      if (!isValidTerm(entry)) {
        invalidEntries.push({ entry, reason: 'Contains unsupported characters' });
        return;
      }
      const key = normalizeTerm(entry);
      if (conflictAlwaysSourceSet.has(key)) {
        invalidEntries.push({ entry, reason: 'Conflicts with Always Translate glossary' });
        return;
      }
      if (existingKeys.has(key)) {
        duplicateEntries.push(entry);
        return;
      }
      existingKeys.add(key);
      validEntries.push(entry);
    });

    if (!validEntries.length) {
      message.warning('No new valid text to add.');
      return;
    }

    const nextRules: TranslateRules = {
      alwaysTranslate: { ...translateRules.alwaysTranslate },
      neverTranslate: { ...translateRules.neverTranslate },
    };
    nextRules.neverTranslate[selectedPair] = [...existingList, ...validEntries];
    const saved = await saveTranslateRules(nextRules, { successMessage: null });
    if (!saved) return;

    clearInput();
    message.success(`Added ${validEntries.length} new entr${validEntries.length > 1 ? 'ies' : 'y'}.`);
    if (invalidEntries.length) {
      const details = invalidEntries.map(({ entry, reason }) => `"${entry}" (${reason})`).join('; ');
      message.warning(
        `Skipped ${invalidEntries.length} invalid entr${invalidEntries.length > 1 ? 'ies' : 'y'}: ${details}`
      );
    }
    if (duplicateEntries.length) {
      message.info(
        `Ignored ${duplicateEntries.length} duplicate entr${duplicateEntries.length > 1 ? 'ies' : 'y'}: ${duplicateEntries.join('; ')}`
      );
    }
  };

  const handleAddNeverTerm = () => {
    void applyNeverTermImport(newNeverTerm, 'Enter at least one word or phrase to add.', () =>
      setNewNeverTerm('')
    );
  };

  const beginEditingNeverTerm = (index: number) => {
    if (!selectedPair) return;
    const term = translateRules.neverTranslate[selectedPair]?.[index] || '';
    setEditingNeverIndex(index);
    setEditingNeverValue(term);
  };

  const handleSaveNeverEdit = () => {
    if (!selectedPair || editingNeverIndex === null) return;
    const term = editingNeverValue.trim();
    if (!term) {
      message.error('Please enter a valid word or phrase.');
      return;
    }
    if (!isValidTerm(term)) {
      message.error(
        'Please enter a valid word or phrase (letters, numbers, spaces, and common punctuation are allowed).'
      );
      return;
    }
    const normalizedKey = normalizeTerm(term);
    const conflictAlwaysSourceSet = getAlwaysSourceSet(translateRules.alwaysTranslate[selectedPair] ?? []);
    if (conflictAlwaysSourceSet.has(normalizedKey)) {
      message.warning('That term already exists in the Always Translate glossary.');
      return;
    }
    const existing = translateRules.neverTranslate[selectedPair] ?? [];
    const duplicate = existing.some((entry, idx) => idx !== editingNeverIndex && normalizeTerm(entry) === normalizedKey);
    if (duplicate) {
      message.warning('That word or phrase already exists.');
      return;
    }
    const nextRules: TranslateRules = {
      alwaysTranslate: { ...translateRules.alwaysTranslate },
      neverTranslate: { ...translateRules.neverTranslate },
    };
    const list = nextRules.neverTranslate[selectedPair] ? [...nextRules.neverTranslate[selectedPair]] : [];
    list[editingNeverIndex] = term;
    nextRules.neverTranslate[selectedPair] = list;
    void saveTranslateRules(nextRules, { successMessage: null });
    setEditingNeverIndex(null);
  };

  const handleRemoveNeverTerm = (index: number) => {
    if (!selectedPair) return;
    const nextRules: TranslateRules = {
      alwaysTranslate: { ...translateRules.alwaysTranslate },
      neverTranslate: { ...translateRules.neverTranslate },
    };
    const list = nextRules.neverTranslate[selectedPair] ? [...nextRules.neverTranslate[selectedPair]] : [];
    list.splice(index, 1);
    nextRules.neverTranslate[selectedPair] = list;
    void saveTranslateRules(nextRules, { successMessage: null });
    setEditingNeverIndex(null);
  };

  const availablePairs = useMemo(() => {
    if (selectedPair) return Array.from(new Set([selectedPair, ...languages]));
    return languages;
  }, [languages, selectedPair]);

  const canSave = Boolean(normalizeSeriesInput(formState.name) && (seriesRecord?.seriesId || incomingSeriesId.trim())) && !saving;

  const loadingMessage =
    loadingContext === 'refresh'
      ? 'Updating series metadata from your books...'
      : loadingContext === 'initial'
        ? 'Creating series page...'
        : 'Loading series data...';

  if (loading) {
    return (
      <PageContainer className="series-management-shell">
        <div className="series-management-state-wrap">
          <div className="series-management-state-panel">
            <Spin size="large" />
            <span>{loadingMessage}</span>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!resolvedName) {
    return (
      <PageContainer className="series-management-shell">
        <div className="series-management-workspace">
          <div className="series-management-state-panel series-management-state-panel--left">
            <div className="series-management-kicker">Series management</div>
            <h1 className="series-management-state-title">No series selected</h1>
            <Paragraph>No series selected. Open this page from Manage Books or a book’s metadata tab.</Paragraph>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/manage-books')}>
              Back to Manage Books
            </Button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="series-management-shell" data-testid="tutorial:series:page">
      <div className="series-management-workspace" data-testid="tutorial:series:workspace">
        <div data-tour-id="series-overview" data-testid="tutorial:series:overview">
          <AppPageHeader
            className="series-management-header"
            kicker="Library"
            title="Manage Series"
            titleId="series-management-title"
            description={`Organize book order, metadata, and series-level changes for ${resolvedName}.`}
            metrics={<span className="series-management-pill">{books.length} {books.length === 1 ? 'book' : 'books'}</span>}
            secondaryActions={(
              <>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/manage-books')}>
              Back to Manage Books
            </Button>
            <Button icon={<QuestionCircleOutlined />} onClick={handleTourStart} data-tour-id="series-tour-trigger" data-testid="tutorial:series:tour" className="app-tour-trigger">
              Take a tour
            </Button>
              </>
            )}
            actions={(
              <>
            <Button
              icon={<ReloadOutlined spin={refreshing} />}
              loading={refreshing || undefined}
              onClick={() => setIsRefreshModalVisible(true)}
              data-tour-id="series-refresh-button"
              data-testid="tutorial:series:refresh"
            >
              Refresh from books
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving || undefined}
              disabled={!canSave}
              className={hasUnsavedChanges && !saving && canSave ? 'series-management-save-pulse' : undefined}
              data-tour-id="series-save-button"
              data-testid="tutorial:series:save"
            >
              Save changes
            </Button>
              </>
            )}
          />
        </div>

        <div className={`series-management-status-row${hasUnsavedChanges ? ' is-warning' : ''}`}>
          <SaveOutlined />
          <span>
            {hasUnsavedChanges
              ? 'Unsaved changes pending. Save changes to update this series.'
              : 'Series changes are saved. Edit fields below when you need to update shared defaults.'}
          </span>
        </div>

        <SeriesSection
          title="Series identity"
          description="Set the shared series name, author, and description inherited by books in this series."
          tourId="series-basics"
        >
          <div className="series-management-form-grid" data-testid="tutorial:series:identity">
            <SeriesField label="Series name">
              <Input
                value={formState.name}
                onChange={e => {
                  setFormState(prev => ({ ...prev, name: e.target.value }));
                  setHasUnsavedChanges(true);
                }}
                placeholder="Series name"
              />
            </SeriesField>
            <SeriesField label="Author">
              <Input
                placeholder="Author"
                value={formState.author}
                onChange={e => {
                  setFormState(prev => ({ ...prev, author: e.target.value }));
                  setHasUnsavedChanges(true);
                }}
              />
            </SeriesField>
            <SeriesField
              label="Series description"
              helper={`${countWordsIncludingMarkup(formState.seriesDescription || '').toLocaleString()} / ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words for translation eligibility.`}
              className="series-management-field--wide"
            >
              <TextArea
                rows={3}
                placeholder="Series description"
                value={formState.seriesDescription}
                onChange={e => {
                  setFormState(prev => ({ ...prev, seriesDescription: e.target.value }));
                  setHasUnsavedChanges(true);
                }}
              />
            </SeriesField>
          </div>
        </SeriesSection>

        <SeriesSection
          title="Description translations"
          description="Translate and review the shared storefront description once for every book in this series."
          tourId="series-description-translations"
        >
          <div className="series-management-description-translation-layout">
            <div className="series-management-description-controls">
              <SeriesField
                label="Source language"
                helper={translationContextBook?.title
                  ? `Detected from ${translationContextBook.title}.`
                  : 'Detected from an attached book.'}
              >
                <Input
                  value={descriptionSourceLanguage || 'Unavailable'}
                  disabled
                  aria-label="Series description source language"
                />
              </SeriesField>
              <SeriesField label="Target language">
                <Select
                  showSearch
                  value={descriptionTargetLanguage || undefined}
                  placeholder="Choose a target language"
                  className="series-management-control"
                  onChange={setDescriptionTargetLanguage}
                  disabled={translatingDescription || savingDescriptionTranslation || descriptionSetupOpen}
                  optionFilterProp="children"
                >
                  {descriptionTargetOptions.map(language => (
                    <Select.Option key={language} value={language}>
                      <Space size="small">
                        <span>{language}</span>
                        {isLanguageBeta(language) && <Tag color="orange">BETA</Tag>}
                      </Space>
                    </Select.Option>
                  ))}
                </Select>
              </SeriesField>

            </div>

            <div className="series-management-description-review" aria-live="polite">
              <div className="series-management-description-review-header">
                <div>
                  <div className="series-management-description-review-title">
                    {descriptionTargetLanguage
                      ? `${descriptionTargetLanguage} description`
                      : 'Choose a language to begin'}
                  </div>
                  {descriptionTargetLanguage && (
                    <div className="series-management-description-review-meta">
                      {savedSeriesDescriptionWordCount.toLocaleString()} source words
                    </div>
                  )}
                </div>
                {descriptionTargetLanguage && (
                  <Tag color={hasCurrentDescriptionTranslation ? 'green' : 'default'}>
                    {hasCurrentDescriptionTranslation ? 'Translated' : 'Not translated'}
                  </Tag>
                )}
              </div>

              {!descriptionTargetLanguage ? (
                <div className="series-management-description-empty">
                  Select a target language to translate or review its shared description.
                </div>
              ) : editingDescriptionTranslation ? (
                <div className="series-management-description-editor">
                  <TextArea
                    value={descriptionDraft}
                    onChange={event => setDescriptionDraft(event.target.value)}
                    autoSize={{ minRows: 6, maxRows: 14 }}
                    placeholder={`Enter the ${descriptionTargetLanguage} series description`}
                    aria-label={`Translated series description in ${descriptionTargetLanguage}`}
                  />
                  <div className={`series-management-description-word-count${descriptionDraftWordCount > MAX_BLURB_TRANSLATION_WORDS ? ' is-error' : ''}`}>
                    {descriptionDraftWordCount.toLocaleString()} / {MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words
                  </div>
                  <Space wrap>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={() => void handleSaveDescriptionTranslation()}
                      loading={savingDescriptionTranslation || undefined}
                      disabled={!descriptionDraft.trim() || descriptionDraftWordCount > MAX_BLURB_TRANSLATION_WORDS}
                    >
                      Save translation
                    </Button>
                    <Button
                      onClick={() => {
                        setDescriptionDraft(currentDescriptionTranslation);
                        setEditingDescriptionTranslation(false);
                      }}
                      disabled={savingDescriptionTranslation}
                    >
                      Cancel
                    </Button>
                  </Space>
                </div>
              ) : (
                <>
                  <div className={`series-management-description-value${hasCurrentDescriptionTranslation ? '' : ' is-empty'}`}>
                    {hasCurrentDescriptionTranslation
                      ? <div dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(currentDescriptionTranslation) }} />
                      : `No ${descriptionTargetLanguage} description has been saved yet.`}
                  </div>
                  <div className="series-management-description-actions">
                    <Button
                      type={hasCurrentDescriptionTranslation ? 'default' : 'primary'}
                      onClick={() => {
                        setDescriptionTranslationError('');
                        setDescriptionSetupOpen(true);
                      }}
                      loading={translatingDescription || undefined}
                      disabled={Boolean(descriptionTranslationBlockedReason) || translatingDescription}
                    >
                      {hasCurrentDescriptionTranslation ? 'Replace with AI' : 'Translate with AI'}
                    </Button>
                    {!hasCurrentDescriptionTranslation && (
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                          setDescriptionDraft('');
                          setEditingDescriptionTranslation(true);
                        }}
                        disabled={!translationContextBook?.book_guid || !descriptionSourceLanguage || hasUnsavedChanges || translatingDescription}
                      >
                        Enter manually
                      </Button>
                    )}
                    {hasCurrentDescriptionTranslation && (
                      <>
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => {
                            setDescriptionDraft(currentDescriptionTranslation);
                            setEditingDescriptionTranslation(true);
                          }}
                          disabled={!translationContextBook?.book_guid || !descriptionSourceLanguage || hasUnsavedChanges || translatingDescription}
                        >
                          Edit translation
                        </Button>
                        <Button icon={<CopyOutlined />} onClick={() => void handleCopyDescriptionTranslation()}>
                          Copy
                        </Button>
                      </>
                    )}
                  </div>
                </>
              )}

              {descriptionTargetLanguage && !descriptionTranslationBlockedReason && (
                <div className="series-management-description-cost-note">
                  {hasCurrentDescriptionTranslation
                    ? `Replacing this translation uses approximately ${savedSeriesDescriptionWordCount.toLocaleString()} credits before model pricing adjustments. Manual edits are free.`
                    : 'The first AI translation for this language pair is included. Manual edits are free.'}
                </div>
              )}
              {descriptionTranslationBlockedReason && (
                <Alert
                  type={seriesDescriptionTooLong ? 'error' : 'warning'}
                  showIcon
                  message="Translation unavailable"
                  description={descriptionTranslationBlockedReason}
                />
              )}
            </div>
          </div>
        </SeriesSection>

        <SeriesSection
          title="Market defaults"
          description="Define reader, genre, trope, and keyword defaults that keep translation and metadata output consistent."
          tourId="series-tags"
        >
          <div className="series-management-form-grid" data-tour-id="series-audience" data-testid="tutorial:series:market-defaults">
            <SeriesField
              label={(
                <Space size="small">
                  <span>Intended audience</span>
                  <AntTooltip
                    title={(
                      <div className="series-management-tooltip-copy">
                        <div>Select a common audience or type your own. Used to tailor tone and word choice in translations.</div>
                        <div>Click to learn more</div>
                      </div>
                    )}
                  >
	                    <Button
	                      type="text"
	                      size="small"
	                      icon={<QuestionCircleOutlined />}
	                      onClick={() => setIsAudienceInfoModalVisible(true)}
	                      data-testid="tutorial:series:audience-info"
	                    />
                  </AntTooltip>
                </Space>
              )}
              helper="Pick common audience labels or type custom reader segments."
            >
              <Select
                mode="tags"
                tokenSeparators={[',']}
                className="series-management-control"
                placeholder="Type or select intended audience"
                value={formState.intendedAudience || []}
                onChange={vals => {
                  setFormState(prev => ({
                    ...prev,
                    intendedAudience: enforceNonfictionAudienceExclusivity(vals as string[]),
                  }));
                  setHasUnsavedChanges(true);
                }}
                popupRender={menu => (
                  <>
                    {menu}
                    <Divider className="series-management-dropdown-divider" />
                    <div className="series-management-audience-presets">
                      {INTENDED_AUDIENCE_PRESETS.map(audience => (
                        <Tag
                          key={audience}
                          className="series-management-audience-preset"
                          onClick={() => {
                            setFormState(prev => ({
                              ...prev,
                              intendedAudience: enforceNonfictionAudienceExclusivity([
                                ...(prev.intendedAudience || []),
                                audience,
                              ]),
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        >
                          {audience}
                        </Tag>
                      ))}
                    </div>
                  </>
                )}
              />
            </SeriesField>

            <SeriesField
              label={(
                <Space size="small">
                  <span>Genres</span>
                  <AntTooltip title={MULTI_ENTRY_TOOLTIP}>
                    <QuestionCircleOutlined />
                  </AntTooltip>
                </Space>
              )}
              helper="Type to add custom genres or paste comma-separated lists."
            >
              <Select
                mode="tags"
                tokenSeparators={[',']}
                value={formState.genre as string[]}
                placeholder="Add genres"
                className="series-management-control"
                onChange={values => {
                  setFormState(prev => ({ ...prev, genre: splitAndDedupeList(values as string[]) }));
                  setHasUnsavedChanges(true);
                }}
              />
            </SeriesField>
            <SeriesField
              label={(
                <Space size="small">
                  <span>Tropes</span>
                  <AntTooltip title={MULTI_ENTRY_TOOLTIP}>
                    <QuestionCircleOutlined />
                  </AntTooltip>
                </Space>
              )}
              helper="Type to add your own tropes or paste comma-separated lists."
            >
              <Select
                mode="tags"
                tokenSeparators={[',']}
                value={formState.tropes as string[]}
                placeholder="Add tropes"
                className="series-management-control"
                onChange={values => {
                  setFormState(prev => ({ ...prev, tropes: splitAndDedupeList(values as string[]) }));
                  setHasUnsavedChanges(true);
                }}
              />
            </SeriesField>
            <SeriesField
              label={(
                <Space size="small">
                  <span>Keywords</span>
                  <AntTooltip title={MULTI_ENTRY_TOOLTIP}>
                    <QuestionCircleOutlined />
                  </AntTooltip>
                </Space>
              )}
              helper="Type to add keywords or paste comma-separated lists from your research."
            >
              <Select
                mode="tags"
                tokenSeparators={[',']}
                value={formState.keywords as string[]}
                placeholder="Add keywords"
                className="series-management-control"
                onChange={values => {
                  setFormState(prev => ({ ...prev, keywords: splitAndDedupeList(values as string[]) }));
                  setHasUnsavedChanges(true);
                }}
              />
            </SeriesField>
          </div>
        </SeriesSection>

        <Modal
          title={<SeriesModalTitle title="Intended audiences" />}
          visible={isAudienceInfoModalVisible}
          onOk={() => setIsAudienceInfoModalVisible(false)}
          onCancel={() => setIsAudienceInfoModalVisible(false)}
          className="series-management-modal"
          footer={[
            <Button key="close" type="primary" onClick={() => setIsAudienceInfoModalVisible(false)}>
              Close
            </Button>,
          ]}
	        >
	          <div className="series-management-modal-stack" data-testid="tutorial:series:audience-info-modal">
            <div className="series-management-modal-block is-muted">
              <p>
                Intended audiences help ScribeShadow tailor translation tone, vocabulary, and reading level for both
                chapters and metadata.
              </p>
              <p>You can add multiple labels or create your own to reflect the readers you are targeting.</p>
            </div>
            <div className="series-management-modal-block">
              <div className="series-management-modal-block-title">Audiences with special translation logic</div>
              <div className="series-management-guidance-list">
                <div><Text strong>Clean Romance Readers:</Text> enables clean-language guardrails: no profanity or religious exclamations, and euphemisms stay mild.</div>
                <div><Text strong>MM Romance Readers:</Text> adds consistency guardrails for male/male romance dynamics and helps prevent accidental feminization or heteronormative rewrites.</div>
                <div><Text strong>FF Romance Readers:</Text> adds consistency guardrails for female/female romance dynamics and helps prevent accidental masculinization or heteronormative rewrites.</div>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <Text strong>Nonfiction - Formal:</Text> uses formal direct reader address in languages that distinguish it,
              such as German Sie/Ihnen/Ihr.
            </div>
            <div style={{ marginTop: 8 }}>
              <Text strong>Nonfiction - Informal:</Text> uses informal direct reader address in languages that distinguish
              it, such as German du/dir/dich/dein.
            </div>
            <div style={{ marginTop: 8 }}>
              Nonfiction reader-address options apply only to narrator or author address to the reader. Character
              dialogue still follows scene context and relationship guidance.
            </div>
          </div>
        </Modal>

        <SeriesSection
          title="Glossary"
          description="Manage language-specific terms and pairs that cascade to every book in this series."
          tourId="series-translate"
        >
          <div className="series-management-form-grid series-management-form-grid--single" data-testid="tutorial:series:glossary-language">
            <SeriesField label="Target language" helper={!selectedPair ? 'Select a language to manage glossary rules.' : undefined}>
              <Select
                showSearch
                value={selectedPair || undefined}
                placeholder="Select a language"
                className="series-management-control"
                onChange={val => setSelectedPair(val)}
                onSelect={val => setSelectedPair(val)}
                allowClear
              >
                {availablePairs.map(lang => (
                  <Select.Option key={lang} value={lang}>
                    <Space size="small">
                      <span>{displayTargetLanguageKey(lang)}</span>
                      {isLanguageBeta(displayTargetLanguageKey(lang)) && <Tag color="orange">BETA</Tag>}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </SeriesField>
          </div>

          {selectedPair && (
            <div className="series-management-glossary-grid" data-testid="tutorial:series:glossary">
              <div className="series-management-glossary-panel" data-testid="tutorial:series:never-translate">
                <div className="series-management-glossary-panel-header">
                  <div>
                    <div className="series-management-glossary-title">
                      Never translate
                      <span className="series-management-count-pill">
                        {currentNeverTerms.length} {currentNeverTerms.length === 1 ? 'term' : 'terms'}
                      </span>
                      <AntTooltip title="We will direct the AI to never translate these words or phrases.">
                        <QuestionCircleOutlined />
                      </AntTooltip>
                    </div>
                    <div className="series-management-glossary-helper">Keep names, places, and special terms in source text.</div>
                  </div>
	                  <Button onClick={() => setIsNeverModalVisible(true)} icon={<EditOutlined />} data-testid="tutorial:series:never-manage">
                    Manage terms
                  </Button>
                </div>
                <div className="series-management-tag-list">
                  {(translateRules.neverTranslate[selectedPair] ?? []).length > 0 ? (
                    neverPreviewTerms.map((word, index) => (
                      <Tag key={`never-inline-${word}-${index}`}>{word}</Tag>
                    ))
                  ) : (
                    <span className="series-management-empty-inline">No glossary terms yet.</span>
                  )}
                  {neverRemainingCount > 0 && (
                    <Button
                      size="small"
                      className="series-management-more-chip"
	                      onClick={() => setIsNeverModalVisible(true)}
	                      data-testid="tutorial:series:never-more"
	                    >
                      +{neverRemainingCount} more
                    </Button>
                  )}
                </div>
              </div>
              <div className="series-management-glossary-panel" data-testid="tutorial:series:always-translate">
                <div className="series-management-glossary-panel-header">
                  <div>
                    <div className="series-management-glossary-title">
                      Always translate
                      <span className="series-management-count-pill">
                        {currentAlwaysPairs.length} {currentAlwaysPairs.length === 1 ? 'pair' : 'pairs'}
                      </span>
                      <AntTooltip title="We will direct the AI to always translate these source terms to their specified target equivalents.">
                        <QuestionCircleOutlined />
                      </AntTooltip>
                    </div>
                    <div className="series-management-glossary-helper">Force exact target equivalents for recurring source terms.</div>
                  </div>
	                  <Button onClick={() => setIsAlwaysModalVisible(true)} icon={<EditOutlined />} data-testid="tutorial:series:always-manage">
                    Manage pairs
                  </Button>
                </div>
                <div className="series-management-tag-list">
                  {(translateRules.alwaysTranslate[selectedPair] ?? []).length > 0 ? (
                    alwaysPreviewPairs.map((word, index) => (
                      <Tag key={`always-inline-${word}-${index}`}>
                        {word.includes('|') ? word.replace('|', ' -> ') : word}
                      </Tag>
                    ))
                  ) : (
                    <span className="series-management-empty-inline">No glossary pairs yet.</span>
                  )}
                  {alwaysRemainingCount > 0 && (
                    <Button
                      size="small"
                      className="series-management-more-chip"
	                      onClick={() => setIsAlwaysModalVisible(true)}
	                      data-testid="tutorial:series:always-more"
	                    >
                      +{alwaysRemainingCount} more
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </SeriesSection>

        <SeriesSection
          title="Books in this series"
          description="Open attached books to review inherited metadata and translation readiness."
          tourId="series-books"
        >
          {books.length === 0 ? (
            <div className="series-management-state-panel series-management-state-panel--compact">
              No books found for this series yet.
            </div>
          ) : (
            <div className="series-management-book-list" data-testid="tutorial:series:books">
              {books.map(item => (
                <div className="series-management-book-row" key={item.book_guid}>
                  <BookCover
                    src={item.coverUrl}
                    className="series-management-book-cover"
                    onClick={() => navigate(`/book-detail/${item.book_guid}`)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${item.title}`}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(`/book-detail/${item.book_guid}`);
                      }
                    }}
                  />
                  <div className="series-management-book-info">
                    <div className="series-management-book-title">{item.title}</div>
                    <div className="series-management-book-meta-pills">
                      <span>{item.series || resolvedName}</span>
                      <span>Book #{item.booknumber || '—'}</span>
                    </div>
                  </div>
                  <Button
                    size="small"
                    icon={<BookOutlined />}
                    onClick={() => navigate(`/book-detail/${item.book_guid}`)}
                  >
                    Open
                  </Button>
                </div>
              ))}
            </div>
          )}
        </SeriesSection>

      {translatingDescription && (
        <div className="app-loading-overlay">
          <ProcessLoadingCard
            title="Translating series description"
            description={`Translating from ${descriptionSourceLanguage} to ${descriptionTargetLanguage} and saving it for every linked book.`}
          />
        </div>
      )}

      <Modal
        open={descriptionSetupOpen}
        title={<div className="translation-modal-titlebar"><span className="translation-modal-titlebar__title">Translate series description</span></div>}
        className="translation-modal-footer-stable series-description-translation-modal"
        width="min(820px, calc(100vw - 32px))"
        onCancel={() => { if (!translatingDescription) setDescriptionSetupOpen(false); }}
        closable={!translatingDescription}
        maskClosable={!translatingDescription}
        keyboard={!translatingDescription}
        footer={[
          <div key="cost" className="translation-modal-footer-summary" aria-live="polite">
            {hasCurrentDescriptionTranslation
              ? `Estimated base cost: ${savedSeriesDescriptionWordCount.toLocaleString()} credits, before model adjustments.`
              : 'First translation included · 0 credits'}
          </div>,
          <Button key="cancel" disabled={translatingDescription} onClick={() => setDescriptionSetupOpen(false)}>Cancel</Button>,
          <Button key="translate" type="primary" className="app-primary-cta"
            loading={translatingDescription}
            disabled={translatingDescription || Boolean(descriptionTranslationBlockedReason) || !descriptionTargetLanguage}
            onClick={handleDescriptionTranslateClick}>
            {translatingDescription ? 'Translating…' : hasCurrentDescriptionTranslation ? 'Review replacement' : 'Translate description'}
          </Button>,
        ]}
      >
        <div className="translation-modal-body">
          <section className="translation-modal-block translation-modal-block--scope">
            <div className="translation-modal-block-header">
              <div><h3>Translation setup</h3><p>{descriptionSourceLanguage} → {descriptionTargetLanguage} · {savedSeriesDescriptionWordCount.toLocaleString()} source words</p></div>
            </div>
            <div className="series-management-form-grid">
              <SeriesField
                label="AI model"
                helper="Model discounts or premiums apply only when replacing an existing translation."
              >
                <AIModelSelect
                  value={descriptionModel}
                  onChange={setDescriptionModel}
                  language={descriptionTargetLanguage}
                  sourceLanguage={descriptionSourceLanguage}
                  workflow="metadata"
                  placeholder="Choose a target language first"
                  disabled={!descriptionTargetLanguage || translatingDescription}
                />
              </SeriesField>
              {descriptionTargetLanguage && hasQuotationOptions(descriptionTargetLanguage) && (
                <SeriesField label="Quotation style">
                  <Select
                    value={descriptionQuotationStyle}
                    className="series-management-control"
                    disabled={translatingDescription}
                    onChange={setDescriptionQuotationStyle}
                  >
                    {quotationOptions.map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label} ({option.marks})
                      </Select.Option>
                    ))}
                  </Select>
                </SeriesField>
              )}
              {descriptionTargetLanguage === 'French' && (
                <SeriesField
                  label="French narrative voice"
                  helper="AI Detect is recommended unless you have a specific market voice in mind."
                >
                  <Select
                    value={descriptionFrenchTone || 'AI Detect'}
                    className="series-management-control"
                    disabled={translatingDescription}
                    onChange={setDescriptionFrenchTone}
                  >
                    <Select.Option value="AI Detect">AI Detect</Select.Option>
                    <Select.Option value="modern">Modern/Accessible</Select.Option>
                    <Select.Option value="classical">Classical/Literary</Select.Option>
                    <Select.Option value="present">Present Tense</Select.Option>
                  </Select>
                </SeriesField>
              )}
            </div>
          </section>
          <section className="translation-modal-block">
            <div className="translation-modal-block-header">
              <div><h3>Source description</h3><p>The translation is saved and shared with every linked book. You can review and edit it when complete.</p></div>
            </div>
            <div className="series-management-description-value" dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(savedSeriesDescription) }} />
          </section>
          {descriptionTranslationError && <Alert type="error" showIcon message="Translation failed" description={descriptionTranslationError} />}
          {descriptionTranslationBlockedReason && <Alert type="warning" showIcon message="Translation unavailable" description={descriptionTranslationBlockedReason} />}
        </div>
      </Modal>

      <Modal
        open={confirmDescriptionReplacement}
        title={(
          <SeriesModalTitle
            kicker="Description translation"
            title={`Replace the ${descriptionTargetLanguage || 'selected'} description?`}
            description="The new translation will replace the shared version on every linked book."
          />
        )}
        okText="Replace translation"
        cancelText="Keep current translation"
        confirmLoading={translatingDescription}
        onOk={() => void runDescriptionTranslation(true)}
        onCancel={() => { if (!translatingDescription) setConfirmDescriptionReplacement(false); }}
        cancelButtonProps={{ disabled: translatingDescription }}
        closable={!translatingDescription}
        maskClosable={!translatingDescription}
        keyboard={!translatingDescription}
        className="series-management-modal"
      >
        <Alert
          type="warning"
          showIcon
          message="This replacement uses credits"
          description={`Estimated base cost: ${savedSeriesDescriptionWordCount.toLocaleString()} credits. Your selected model's discount or premium will be applied before the credit hold is created.`}
        />
      </Modal>

      <Modal
        open={isRefreshModalVisible}
        title={<SeriesModalTitle title="Refresh from books?" description="Choose which series defaults to replace with combined metadata from attached books." />}
        onOk={async () => {
          if (selectedRefreshFields.length === 0) {
            message.warning('Select at least one field to refresh.');
            return;
          }
          setIsRefreshModalVisible(false);
          await handleRefresh(selectedRefreshFields);
        }}
        onCancel={() => setIsRefreshModalVisible(false)}
        okText="Refresh"
        cancelText="Cancel"
        confirmLoading={refreshing}
        className="series-management-modal"
      >
	        <div className="series-management-modal-stack" data-testid="tutorial:series:refresh-modal">
	          <div className="series-management-modal-block is-muted">
	            Refreshing can overwrite selected series defaults with values aggregated from books in this series.
	          </div>
	          <div className="series-management-modal-block" data-testid="tutorial:series:refresh-fields">
            <div className="series-management-modal-block-title">Fields to refresh</div>
            <Checkbox.Group
              value={selectedRefreshFields}
              onChange={vals => setSelectedRefreshFields(vals as RefreshableField[])}
              className="series-management-refresh-checklist"
            >
              {REFRESHABLE_FIELDS.map(field => (
                <Checkbox key={field} value={field}>
                  {REFRESH_FIELD_LABELS[field]}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
          <div className="series-management-modal-note">
            Fields you leave unchecked will keep their current values.
          </div>
        </div>
      </Modal>

      <Modal
        open={isAlwaysModalVisible}
        title={<SeriesModalTitle kicker="Glossary" title={`Always translate • ${selectedPair}`} description="Map recurring source terms to exact target-language equivalents." />}
        onCancel={closeAlwaysModal}
        footer={[
	          <Button key="close" onClick={closeAlwaysModal} data-testid="tutorial:series:always-close">
            Close
          </Button>,
        ]}
        width={720}
        className="series-management-modal"
      >
        <Spin spinning={updatingRules}>
	          <div className="series-management-modal-stack" data-testid="tutorial:series:always-modal">
	            <div className="series-management-modal-block is-muted" data-testid="tutorial:series:always-add">
              <div className="series-management-modal-block-title">Add pair</div>
              <div className="series-management-modal-grid">
                <SeriesField label="Source term">
                  <Input
                    value={newAlwaysSource}
                    onChange={e => setNewAlwaysSource(e.target.value)}
                    placeholder="Enter source term"
                  />
                </SeriesField>
                <SeriesField label="Target term">
                  <Input
                    value={newAlwaysTarget}
                    onChange={e => setNewAlwaysTarget(e.target.value)}
                    onPressEnter={handleAddAlwaysPair}
                    placeholder="Enter target term"
                  />
                </SeriesField>
              </div>
              <Button icon={<PlusOutlined />} type="primary" onClick={handleAddAlwaysPair}>
                Add glossary pair
              </Button>
            </div>

	            <div className="series-management-modal-block" data-testid="tutorial:series:always-bulk-import">
              <div className="series-management-modal-block-title">Bulk import pairs</div>
              <div className="series-management-modal-description">
                Use the format <code>source ~ target | source2 ~ target2</code> or separate pairs with new lines.
              </div>
              <TextArea
                rows={3}
                value={bulkAlwaysInput}
                onChange={e => setBulkAlwaysInput(e.target.value)}
                placeholder="e.g., Coffee ~ Kopi | Tea ~ Teh"
              />
              <Button
                className="series-management-modal-inline-action"
                onClick={handleBulkAlwaysImport}
                disabled={!bulkAlwaysInput.trim()}
              >
                Import Pairs
              </Button>
            </div>

	            <div className="series-management-modal-block is-info" data-testid="tutorial:series:always-list">
	              <div className="series-management-modal-toolbar" data-testid="tutorial:series:always-toolbar">
                <div>
                  <strong>Glossary pairs</strong>
                  <AntTooltip title="We will direct the AI to always translate these source terms to their specified target equivalents.">
                    <QuestionCircleOutlined />
                  </AntTooltip>
                </div>
                <Space size="small" wrap>
                  <AntTooltip title="Copy as a readable list (each pair on its own line)">
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => handleCopyAlwaysPairs('readable')}
                      disabled={!currentAlwaysPairs.length}
                    >
                      Copy List
                    </Button>
                  </AntTooltip>
                  <AntTooltip title="Copy in bulk import format (source ~ target | source2 ~ target2)">
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => handleCopyAlwaysPairs('bulk')}
                      disabled={!currentAlwaysPairs.length}
                    >
                      Copy Import String
                    </Button>
                  </AntTooltip>
                </Space>
              </div>
	              <Input
	                value={alwaysSearch}
	                onChange={e => setAlwaysSearch(e.target.value)}
	                placeholder="Search pairs"
	                className="series-management-modal-search"
	                data-testid="tutorial:series:always-search"
	              />
              <div className="series-management-modal-scroll">
                {selectedPair && currentAlwaysPairs.length > 0 ? (
                  <div className="series-management-glossary-row-list">
                    {filteredAlwaysPairs.map(({ index, source, target, term }) => {
                      const isEditing = editingAlwaysIndex === index;
                      return (
                        <div
                          key={`always-${term}-${index}`}
                          className={`series-management-glossary-row${isEditing ? ' is-editing' : ''}`}
                        >
                          {isEditing ? (
                            <div className="series-management-glossary-edit-row">
                              <Input
                                value={editingAlwaysSource}
                                onChange={e => setEditingAlwaysSource(e.target.value)}
                                placeholder="Source"
                              />
                              <Input
                                value={editingAlwaysTarget}
                                onChange={e => setEditingAlwaysTarget(e.target.value)}
                                placeholder="Target"
                              />
                              <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSaveAlwaysEdit}>
                                Save
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="series-management-glossary-row-main">
                                <span className="series-management-glossary-cell">
                                  <span className="series-management-glossary-cell-label">Source</span>
                                  {source}
                                </span>
                                <span className="series-management-glossary-cell">
                                  <span className="series-management-glossary-cell-label">Target</span>
                                  {target}
                                </span>
                              </div>
                              <div className="series-management-glossary-row-actions">
                                <Button size="small" icon={<EditOutlined />} onClick={() => beginEditingAlwaysPair(index)}>
                                  Edit
                                </Button>
                                <Button size="small" className="series-management-danger-action" icon={<DeleteOutlined />} onClick={() => handleRemoveAlwaysPair(index)}>
                                  Delete
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                    {filteredAlwaysPairs.length === 0 && (
                      <div className="series-management-empty-inline">No glossary pairs matched your search.</div>
                    )}
                  </div>
                ) : (
                  <div className="series-management-empty-inline">
                    No glossary pairs added yet. Add your first pair above.
                  </div>
                )}
              </div>
            </div>
          </div>
        </Spin>
      </Modal>

      <Modal
        open={isNeverModalVisible}
        title={<SeriesModalTitle kicker="Glossary" title={`Never translate • ${selectedPair}`} description="Keep names, locations, brands, and special terms in source text." />}
        onCancel={closeNeverModal}
        footer={[
	          <Button key="close" onClick={closeNeverModal} data-testid="tutorial:series:never-close">
            Close
          </Button>,
        ]}
        width={720}
        className="series-management-modal"
      >
        <Spin spinning={updatingRules}>
	          <div className="series-management-modal-stack" data-testid="tutorial:series:never-modal">
	            <div className="series-management-modal-block is-muted" data-testid="tutorial:series:never-add">
              <SeriesField
                label="Glossary term(s) to keep in source"
                helper={<>Separate entries with commas, pipes, or new lines (example: <em>Hero, Sidekick, Location</em>).</>}
              >
                <Input
                  placeholder="Enter words or phrases to keep"
                  value={newNeverTerm}
                  onChange={e => setNewNeverTerm(e.target.value)}
                  onPressEnter={handleAddNeverTerm}
                />
              </SeriesField>
              <Button icon={<PlusOutlined />} type="primary" onClick={handleAddNeverTerm}>
                Add glossary terms
              </Button>
            </div>

	            <div className="series-management-modal-block is-info" data-testid="tutorial:series:never-list">
	              <div className="series-management-modal-toolbar" data-testid="tutorial:series:never-toolbar">
                <div>
                  <strong>Glossary terms</strong>
                  <AntTooltip title="We will direct the AI to never translate these words or phrases.">
                    <QuestionCircleOutlined />
                  </AntTooltip>
                </div>
                <Space size="small" wrap>
                  <AntTooltip title="Copy as a readable list (one per line)">
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => handleCopyNeverTerms('readable')}
                      disabled={!currentNeverTerms.length}
                    >
                      Copy List
                    </Button>
                  </AntTooltip>
                  <AntTooltip title="Copy in bulk format (term1 | term2 | term3)">
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => handleCopyNeverTerms('pipe')}
                      disabled={!currentNeverTerms.length}
                    >
                      Copy Import String
                    </Button>
                  </AntTooltip>
                </Space>
              </div>
	              <Input
	                value={neverSearch}
	                onChange={e => setNeverSearch(e.target.value)}
	                placeholder="Search terms"
	                className="series-management-modal-search"
	                data-testid="tutorial:series:never-search"
	              />
              <div className="series-management-modal-scroll">
                {selectedPair && currentNeverTerms.length > 0 ? (
                  <div className="series-management-glossary-row-list">
                    {filteredNeverTerms.map(({ index, term }) => {
                      const isEditing = editingNeverIndex === index;
                      return (
                        <div
                          key={`never-${term}-${index}`}
                          className={`series-management-glossary-row${isEditing ? ' is-editing' : ''}`}
                        >
                          {isEditing ? (
                            <div className="series-management-glossary-edit-row series-management-glossary-edit-row--single">
                              <Input
                                value={editingNeverValue}
                                onChange={e => setEditingNeverValue(e.target.value)}
                                placeholder="Term"
                              />
                              <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSaveNeverEdit}>
                                Save
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="series-management-glossary-row-main">
                                <span className="series-management-glossary-cell">
                                  <span className="series-management-glossary-cell-label">Term</span>
                                  {term}
                                </span>
                              </div>
                              <div className="series-management-glossary-row-actions">
                                <Button size="small" icon={<EditOutlined />} onClick={() => beginEditingNeverTerm(index)}>
                                  Edit
                                </Button>
                                <Button size="small" className="series-management-danger-action" icon={<DeleteOutlined />} onClick={() => handleRemoveNeverTerm(index)}>
                                  Delete
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                    {filteredNeverTerms.length === 0 && (
                      <div className="series-management-empty-inline">No glossary terms matched your search.</div>
                    )}
                  </div>
                ) : (
                  <div className="series-management-empty-inline">
                    No glossary terms added yet. Add your first term above.
                  </div>
                )}
              </div>
            </div>
          </div>
        </Spin>
      </Modal>
      {hasUnsavedChanges && (
        <div className="series-management-dirty-bar" role="status">
          <div>
            <strong>Unsaved changes pending</strong>
            <span>Save changes to update this series.</span>
          </div>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving || undefined}
            disabled={!canSave}
          >
            Save changes
          </Button>
        </div>
      )}
      </div>
      <AppTour
        open={isTourOpen}
        steps={seriesTourSteps}
        current={tourStep}
        onChange={handleTourChange}
        onClose={handleTourClose}
        onFinish={handleTourClose}
        zIndex={2000}
      />
    </PageContainer>
  );
};

export default SeriesManagementPage;
